

import * as THREE from '/assets/scripts/three.module.js';
import { getStructuredOverlay, createCaseSpatialState, getActualModes } from './structured-state.js?v=20260817-relbridge9';
import { buildSpatialPlan, chooseSnapCandidate, cycleSnapCandidate, applyRankCandidateToPlan, componentPlacementStatus } from '../spatial/snap-engine.js?v=20260817-pcieanchor10';
import { rankSpatialSolutions, spatialRankerStatus } from '../spatial/gnn-placement-ranker.js?v=20260816-3dperf1';
import { cycleAllowedOrientation, nextCoolerQuarterTurn, coolerRelativeQuaternion, applyOrientationClass, orientationClassFromObject3D, orientationMatrixForClass } from '../spatial/orientation-profiles.js?v=20260817-relbridge9';
import { createProceduralComponent, inferComponentMeta } from '../components/factory.js?v=20260817-pcieanchor10';
import { MOTHERBOARD_PCB_THICKNESS } from '../spatial/geometry.js';
import { socketLandmarkLocal } from './motherboard-landmarks.js?v=20260817-pcieanchor10';
import { defaultMotherboardRamSlotCount, normalizeMotherboardFormFactor } from '../spatial/motherboard-standard.js?v=20260817-pcieanchor10';
import { createSpatialSnapshot } from './spatial-snapshot.js?v=20260817-relbridge9';
import { componentSlotForTrackType, componentsInSameSlot, resetComponentSpatialOverrides, primaryComponentList } from './component-slots.js';
import { snapshotPrimaryHardware, stableSolveFingerprint } from './solve-state.js';
import { evaluateHardwareEligibility } from './hardware-eligibility.js';
import { calculateCleanBox, reconcileComponentInsideCase as reconcileComponentWithinCase, createHardwareBoxMesh } from '../scene/object-geometry.js?v=20260817-relbridge9';
import { SVG_LOCK_CLOSED, SVG_LOCK_OPEN } from '../ui/manifest.js?v=20260817-boardfan3';
import { slugifyUrlSegment, caseRouteSlug, normalizePresetName, formatCaseBrandTitle } from '../ui/case-navigation.js?v=20260816-mainref1';
import { renderCaseSpatialEvidenceMarkers, buildCaseFanSnapTargets, placeFanOnCaseSpatialTarget, annotateRadiatorCandidatesWithSpatialEvidence, caseFanTargetBlockers } from './spatial-evidence.js?v=20260817-boardzones4';
import { createAioAssemblyManager } from './aio-assembly.js?v=20260820-perf2';
import { evaluateRamCoolerCompatibility, ramCoolerPairCompatibility, ramHeightFrom } from './ram-clearance.js?v=20260817-boardfan3';
import { ramKitBoardPlacement, normalizeRamSlotCount } from './ram-layout.js?v=20260817-pcieanchor10';

const PRESET_COLORS = {
  'fractal design terra': 0x16a34a, 'terra': 0x16a34a,
  'fractal design ridge': 0x7c3aed, 'ridge': 0x7c3aed,
  'formd t1': 0x007dec, 'sandwich': 0x007dec, 't1 sandwich': 0x007dec,
  'ncase m1': 0xea580c, 'm1 v6.1': 0xea580c,
  'ncase m2': 0x1e293b, 'm2': 0x1e293b,
  'nfc s5m': 0x0891b2, 's5m': 0x0891b2, 'skyreach': 0x0891b2,
  'nr200': 0x0d9488, 'cooler master': 0x0d9488,
  'dan cases': 0xf43f5e, 'dan a4-sfx': 0xf43f5e, 'a4-sfx': 0xf43f5e,
  'meshroom': 0xf59e0b, 'ssupd': 0xf59e0b
};

const EXPANDED_COLOR_POOL = [
  0x38bdf8, 0x10b981, 0xc084fc, 0xea580c, 0x0891b2, 0xf43f5e, 0xf59e0b, 0x0d9488
];

export function createSimulatorWorkbenchController({
  activeBoxes,
  scene,
  controls,
  workbenchRuntime,
  manifestUi,
  buildUnifiedHardwareLabel,
  getNearFitController = () => null,
  getInteractionController = () => null,
  getCasesDataset = () => [],
  getStructuredIndex = () => ({ byName:new Map(), byKey:new Map(), count:0 }),
  getCaseIndexBySlug = () => new Map(),
  getCaseIndexByNormalizedName = () => new Map(),
  getCatalogDiagnostics = () => ({})
} = {}) {
  if (!Array.isArray(activeBoxes)) throw new Error('createSimulatorWorkbenchController requires activeBoxes');
  if (!scene) throw new Error('createSimulatorWorkbenchController requires scene');

  let activeEngineMode = 'workbench';
  let simulatorSolveEpoch = 0;
  let lastSpatialSolveFingerprint = 'no-case';
  let pendingRadiatorFanReplacement = null;
  let aioAssemblyManager = null;

  let HARDWARE_ELIGIBILITY_PLAN = null;
  let HARDWARE_ELIGIBILITY_CACHE = {
    motherboard: new WeakMap(),
    psu: new WeakMap(),
    gpu: new WeakMap(),
    aircooler: new WeakMap(),
    ram: new WeakMap(),
    radiator: new WeakMap()
  };

function htmlSafe(value) {
  return String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
}
function activeCaseBox() { return activeBoxes.find(b => b.isCase) || null; }

function activeRamModule() { return activeBoxes.find(b => b?.isComponent && b.trackType === 'ram') || null; }
function activeMotherboard() { return activeBoxes.find(b => b?.isComponent && b.trackType === 'motherboard') || null; }
function activeAirCooler() { return activeBoxes.find(b => b?.isComponent && b.trackType === 'aircooler') || null; }

function hardwareEligibility(trackType, dims = {}, meta = {}) {
  if(trackType==='ram'){
    const board=activeMotherboard();
    const boardType=String(board?.componentMeta?.ramType||'').trim().toUpperCase();
    const memoryType=String(meta?.memoryType||'').trim().toUpperCase();
    if(boardType&&memoryType&&boardType!==memoryType){
      return {allowed:false,reason:`${memoryType} RAM is incompatible with ${boardType} motherboard`,code:'ram_type_mismatch'};
    }
    const cooler=activeAirCooler();
    if(cooler){
      const pair=evaluateRamCoolerCompatibility(ramHeightFrom(dims,meta),cooler.componentMeta||{});
      if(!pair.allowed)return pair;
    }
  } else if(trackType==='motherboard'){
    const ram=activeRamModule();
    const memoryType=String(ram?.componentMeta?.memoryType||'').trim().toUpperCase();
    const boardType=String(meta?.ramType||'').trim().toUpperCase();
    if(boardType&&memoryType&&boardType!==memoryType){
      return {allowed:false,reason:`${boardType} motherboard is incompatible with selected ${memoryType} RAM`,code:'ram_type_mismatch'};
    }
  } else if(trackType==='aircooler'){
    const ram=activeRamModule();
    if(ram){
      const pair=evaluateRamCoolerCompatibility(ramHeightFrom(ram),meta);
      if(!pair.allowed)return pair;
    }
  }
  const box=activeCaseBox();
  const plan=SFF_SimulatorEngine?.activeBlueprint || box?.spatialPlan || null;
  if(activeEngineMode!=='simulator' || !box || !plan)return {allowed:true,reason:'no_active_case'};

  // Selection availability and build validity are deliberately separate contracts.
  // Motherboard/PSU replacement is gated only by declared case capability here; the actual
  // transaction then runs one full relational solve and may move/park conflicting hardware.
  // This also removes the previous 8x dropdown-refresh amplification from prospective solves.
  return evaluateHardwareEligibility({trackType,dims,meta,plan,caseState:box.spatialState || null});
}

function refreshHardwareEligibilityUi() {
  const selector=document.getElementById('sff-component-selector');
  if(!selector)return;
  const defs={
    itxmobo:{trackType:'motherboard',dims:{l:170,w:170,h:44},meta:{formFactor:'ITX'}},
    dtxmobo:{trackType:'motherboard',dims:{l:203,w:170,h:44},meta:{formFactor:'DTX'}},
    matxmobo:{trackType:'motherboard',dims:{l:244,w:244,h:44},meta:{formFactor:'MATX'}},
    atxmobo:{trackType:'motherboard',dims:{l:305,w:244,h:44},meta:{formFactor:'ATX'}},
    sfxpsu:{trackType:'psu',dims:{l:125,w:63.5,h:100},meta:{formFactor:'SFX'}},
    sfxlpsu:{trackType:'psu',dims:{l:125,w:63.5,h:125},meta:{formFactor:'SFX-L'}},
    atxpsu:{trackType:'psu',dims:{l:150,w:86,h:140},meta:{formFactor:'ATX'}},
    flexpsu:{trackType:'psu',dims:{l:150,w:40.5,h:81.5},meta:{formFactor:'FLEX'}}
  };
  [...selector.options].forEach(option=>{
    const def=defs[option.value]; if(!def)return;
    const result=hardwareEligibility(def.trackType,def.dims,def.meta);
    option.disabled=!result.allowed;
    option.title=result.allowed?'':result.reason;
  });
}

function hardwareDatasetEligibility(trackType,row){
  if(!row)return false;
  const plan=SFF_SimulatorEngine?.activeBlueprint || null;
  if(plan!==HARDWARE_ELIGIBILITY_PLAN){
    HARDWARE_ELIGIBILITY_PLAN=plan;
    HARDWARE_ELIGIBILITY_CACHE={
      motherboard:new WeakMap(), psu:new WeakMap(), gpu:new WeakMap(), aircooler:new WeakMap(), ram:new WeakMap(), radiator:new WeakMap()
    };
  }
  const cache=HARDWARE_ELIGIBILITY_CACHE[trackType];
  const dynamicRamPair=trackType==='ram'||trackType==='aircooler';
  if(!dynamicRamPair&&cache?.has(row))return cache.get(row);

  const dims={l:Number(row.l)||0,w:Number(row.w)||0,h:Number(row.h)||0};
  let meta={};
  if(trackType==='radiator') meta={
    catalogId:row.catalogId||row.id||'',
    radiatorSize:Number(row.radiatorSize)||0,
    fanProfile:row.fanProfile||row.radFan||'standard',
    radFan:row.radFan||row.fanProfile||'standard',
    fanThickness:Number(row.fanThickness)||25,
    fanSize:Number(row.fanSize)||0,
    fanCount:Number(row.fanCount)||0,
    aioBlock:row.aioBlock?{...row.aioBlock,dimensionKnown:{...(row.aioBlock.dimensionKnown||{})}}:null,
    tubeLength:Number(row.tubeLength)||350,
    tubeLengthKnown:!!row.tubeLengthKnown,
    tubeLengthAssumed:!!row.tubeLengthAssumed || !row.tubeLengthKnown,
    pumpLocation:row.pumpLocation||'',
    dimensionKnown:row.dimensionKnown||null,
    dimensionSource:row.dimensionSource||'unknown'
  };
  else if(trackType==='gpu') meta={catalogId:row.catalogId||row.id||'',dimensionKnown:row.dimensionKnown||null,gpuHeightSource:row.gpuHeightSource||'catalog_width'};
  else if(trackType==='aircooler') meta={catalogId:row.catalogId||row.id||'',ramClearance:Number(row.ramClearance)||0,ramClearanceKnown:!!row.ramClearanceKnown,ramClearanceOpen:!!row.ramClearanceOpen,coolerType:row.coolerType||'',dimensionKnown:row.dimensionKnown||null,dimensionSource:row.dimensionSource||'unknown'};
  else if(trackType==='ram') meta={catalogId:row.catalogId||row.id||'',ramHeight:Number(row.ramHeight||row.h)||0,ramHeightKnown:!!row.ramHeightKnown,memoryType:row.memoryType||'',rgb:!!row.rgb,ramKitModules:Number(row.ramKitModules)||2,dimensionKnown:row.dimensionKnown||null,dimensionSource:row.dimensionSource||'catalog_ram_height_visual_udimm_envelope'};
  else if(trackType==='motherboard') meta={catalogId:row.catalogId||row.id||'',formFactor:row.formFactor||'ITX',brand:row.brand||'',model:row.model||'',socket:row.socket||'',chipset:row.chipset||'',ramSlots:Number(row.ramSlots)||defaultMotherboardRamSlotCount(row.formFactor||'ITX'),ramType:row.ramType||'',dimensionKnown:row.dimensionKnown||null,dimensionSource:row.dimensionSource||'unknown'};
  else if(trackType==='psu') meta={catalogId:row.catalogId||row.id||'',formFactor:row.formFactor||'SFX',brand:row.brand||'',model:row.model||'',wattage:Number(row.wattage)||0,dimensionKnown:row.dimensionKnown||null,dimensionSource:row.dimensionSource||'unknown'};
  const allowed=hardwareEligibility(trackType,dims,meta).allowed;
  if(!dynamicRamPair)cache?.set(row,allowed);
  return allowed;
}

function updateComponentLockUi(comp) {
  const btn = document.getElementById(`lock_btn_${comp?.uid}`);
  if (!btn) return;
  const caseMounted=comp?.trackType==='fan' && !!comp?.attachedToCaseUid;
  btn.innerHTML = caseMounted
    ? `${SVG_LOCK_CLOSED} <span>Case Mount</span>`
    : comp?.isLockedToCase
      ? `${SVG_LOCK_CLOSED} <span>Locked</span>`
      : `${SVG_LOCK_OPEN} <span>Unlocked</span>`;
  btn.style.color = (caseMounted || comp?.isLockedToCase) ? '#38bdf8' : 'var(--sub-text)';
}
function combinedRuntimeAdvisories(comp) {
  const seen=new Set();
  const out=[];
  for(const item of [...(comp?.spatialEvidenceAdvisories||[]),...(comp?.aioAssemblyAdvisories||[])]){
    const key=`${item?.type||''}|${item?.message||''}`;
    if(seen.has(key))continue;
    seen.add(key);out.push(item);
  }
  if(comp)comp.runtimeAdvisories=out;
  return out;
}

function updateComponentPlacementUi(comp) {
  const el=document.getElementById(`placement_status_${comp?.uid}`);
  if(!el)return;
  const state=comp?.runtimeContainment||{};
  if(state.status==='unplaced'){
    el.style.display='block';
    el.style.color='#f59e0b';
    el.textContent=`AUTO-PLACEMENT FAILED · ${String(state.reason||'NO VALID CANDIDATE').replace(/_/g,' ').toUpperCase()}`;
    el.title=Array.isArray(state.reasons)?state.reasons.join(' · '):String(state.reason||'');
    getNearFitController()?.reportFailure?.({
      trackType:comp?.trackType,
      label:comp?.displayLabel || comp?.badgeEl?.textContent || comp?.trackType || 'Hardware',
      dims:comp?.nativeSpecs || {},
      meta:comp?.componentMeta || {},
      reason:state.reason || 'no_hard_valid_candidate'
    });
  }else{
    const advisories=combinedRuntimeAdvisories(comp);
    if(advisories.length){
      el.style.display='block';
      el.style.color='#f59e0b';
      el.textContent=`ADVISORY · ${advisories.map(item=>String(item?.message||item?.type||'CHECK FIT')).join(' · ')}`;
      el.title='Advisory only — source/primitive geometry does not change deterministic fit authority.';
      return;
    }
    el.style.display='none';
    el.textContent='';
    el.title='';
    return;
  }
}

aioAssemblyManager=createAioAssemblyManager({
  scene,
  activeBoxes,
  getCaseBox:activeCaseBox,
  onAdvisoryChange:updateComponentPlacementUi,
  markRenderDirty:()=>workbenchRuntime?.markRenderDirty?.()
});

function prepareComponentsForFreshSolve(reason = 'case_change') {
  activeBoxes.forEach(comp => {
    if (!comp?.isComponent) return;
    if (scene && comp.parent && comp.parent !== scene) scene.attach(comp);
    comp.isLockedToCase = false;
    if(comp.trackType==='fan' && comp.attachedToCaseUid){
      comp.attachedToCaseUid=null;
      comp.caseFanMountId=null;
      comp.caseFanMountSlot=null;
      comp.caseFanMountCount=null;
      comp.caseFanMountFrame=null;
      comp.caseFanMountZone=null;
      comp.caseFanMountFace=null;
      comp.activeFanSnapId=null;
    }
    resetComponentSpatialOverrides(comp);
    // Case/mode coordinate-frame changes are the deliberate boundary where a prior manual
    // XYZ adjustment stops being authoritative. Hardware inventory changes no longer call
    // this function, so their manual placements remain sticky.
    comp.spatialUserAdjusted = false;
    comp.lastSpatialResetReason = reason;
    comp.updateMatrixWorld?.(true);
    updateComponentLockUi(comp);
  });
}

function removePrimarySimulatorComponentsForTransaction() {
  const current = primaryComponentList(activeBoxes);
  current.forEach(comp => window.removeModelInstance?.(comp.uid, { suppressRebuild: true, internalTransaction: true }));
}

function spawnHardwareSnapshot(snapshot, { suppressRebuild = true } = {}) {
  if (!snapshot?.trackType) return null;
  return createComponentInstance({
    targetL: snapshot.dims?.l,
    targetW: snapshot.dims?.w,
    targetH: snapshot.dims?.h,
    labelName: snapshot.label,
    isCompFlag: true,
    trackingSizeStr: `${snapshot.dims?.l || 0} x ${snapshot.dims?.w || 0} x ${snapshot.dims?.h || 0} mm`,
    trackingColor: snapshot.color,
    trackType: snapshot.trackType,
    componentMetaOverride: snapshot.componentMeta,
    suppressRebuild
  });
}

function applyCatalogSpatialEvidence(plan, caseBox) {
  if(!plan||!caseBox)return plan;
  const radiator=activeBoxes.find(item=>item?.isComponent&&item.trackType==='radiator')||null;
  const radiatorThickness=Number(radiator?.nativeSpecs?.h)||0;
  const fanThickness=Number(radiator?.componentMeta?.fanThickness)||0;
  return annotateRadiatorCandidatesWithSpatialEvidence(
    plan,
    caseBox.catalogRecord || {},
    Number(radiator?.componentMeta?.radiatorSize)||0,
    {
      radiatorThickness,
      stackThickness:radiatorThickness+(fanThickness||0)
    }
  );
}

function replaceSimulatorSlotIfNeeded(trackType) {
  if (activeEngineMode !== 'simulator') return;
  if(trackType==='ram'){
    activeBoxes.filter(comp=>comp?.isComponent&&comp.trackType==='ram')
      .forEach(comp=>window.removeModelInstance?.(comp.uid,{suppressRebuild:true,internalTransaction:true}));
    return;
  }
  const slot = componentSlotForTrackType(trackType);
  if (!slot) return;
  const stale = componentsInSameSlot(activeBoxes, trackType);
  stale.forEach(comp => window.removeModelInstance?.(comp.uid, { suppressRebuild: true }));
}
async function requestGnnSpatialRerank(caseBox, basePlan, epoch, fingerprint, realignUid = null, realignPolicy = 'all') {
  const solved=basePlan?.solver;
  if(!caseBox||!solved?.rankingCandidates?.length)return;
  const result=await rankSpatialSolutions(caseBox.catalogRecord||caseBox.nativeSpecs,basePlan.effective,solved);
  if(!result)return;
  const liveCase=activeCaseBox();
  if(!liveCase||liveCase.uid!==caseBox.uid)return;
  if(simulatorSolveEpoch!==epoch||lastSpatialSolveFingerprint!==fingerprint)return;
  const rankCandidate=solved.rankingCandidates.find(c=>c.rankId===result.candidateId) || solved.rankingCandidates[result.bestIndex];
  if(!rankCandidate?.hardValid)return;
  const rankedPlan=applyCatalogSpatialEvidence(applyRankCandidateToPlan(basePlan,rankCandidate,{
    bestScore:result.bestScore,secondScore:result.secondScore,gap:result.gap,modelPath:result.modelPath||null
  }),liveCase);
  rankedPlan.solveEpoch=epoch;
  rankedPlan.solveReason=basePlan.solveReason;
  rankedPlan.solveFingerprint=fingerprint;
  SFF_SimulatorEngine.activeBlueprint=rankedPlan;
  liveCase.spatialPlan=rankedPlan;
  workbenchRuntime?.markSpatialDirty?.();
  updateCaseTopologyStatus(liveCase);
  applyBlueprintRealignment(epoch,{policy:realignPolicy,targetUid:realignUid});
}

function rebuildSimulatorPlan({ realign = true, reason = 'rebuild', realignUid = null, realignPolicy = 'all' } = {}) {
  if (activeEngineMode !== 'simulator') return null;
  const caseBox = activeCaseBox();
  if (!caseBox) return null;
  const epoch = ++simulatorSolveEpoch;
  const fingerprint = stableSolveFingerprint(caseBox, activeBoxes);
  const solveStartedAt = workbenchRuntime?.now?.() ?? Date.now();
  const nextPlan = applyCatalogSpatialEvidence(SFF_SimulatorEngine.calculateInternalMap(caseBox.nativeSpecs),caseBox);
  const solveMs = (workbenchRuntime?.now?.() ?? Date.now()) - solveStartedAt;
  workbenchRuntime?.recordSolver?.(solveMs, reason);
  if (!nextPlan) return null;
  nextPlan.solveEpoch = epoch;
  nextPlan.solveReason = reason;
  nextPlan.solveFingerprint = fingerprint;
  lastSpatialSolveFingerprint = fingerprint;
  SFF_SimulatorEngine.activeBlueprint = nextPlan;
  caseBox.spatialPlan = nextPlan;
  workbenchRuntime?.markSpatialDirty?.();
  SFF_SimulatorEngine.renderEnvelopes(caseBox);
  updateCaseTopologyStatus(caseBox);
  refreshHardwareEligibilityUi();
  getNearFitController()?.notifyBuildChanged?.();
  if (realign) applyBlueprintRealignment(epoch,{policy:realignPolicy,targetUid:realignUid});
  // GNN is advisory placement intelligence only. The deterministic plan is applied
  // immediately as a fallback; a model result may replace it only with a candidate that
  // already passed hard geometry validation. Preserve the same realignment policy when an
  // advisory candidate arrives later so it cannot undo a user's manual translation.
  requestGnnSpatialRerank(caseBox,nextPlan,epoch,fingerprint,realignUid,realignPolicy);
  return nextPlan;
}

// =========================================================================
// DETERMINISTIC SPATIAL ENGINE ADAPTER
// =========================================================================
const SFF_SimulatorEngine = {
  activeBlueprint: null,
  clearanceHelpers: [],
  calculateInternalMap(caseSpecs) {
    const caseBox = activeCaseBox();
    const record = caseBox?.catalogRecord || caseSpecs || {};
    return buildSpatialPlan(record, caseBox?.structuredOverlay || null, caseBox?.spatialState || createCaseSpatialState(record, null), activeBoxes);
  },
  renderEnvelopes(_caseGroup) {
    // Constraint envelopes stay in the spatial plan but are intentionally not rendered.
    // The Workbench now shows only the case and clean category-colour component boxes.
    this.clearClearanceHelpers();
  },
  clearClearanceHelpers() {
    this.clearanceHelpers.forEach(mesh => {
      mesh.parent?.remove(mesh);
      workbenchRuntime?.disposeObject3DResources?.(mesh);
    });
    this.clearanceHelpers=[];
    workbenchRuntime?.markRenderDirty?.();
  }
};

function loadCatalogHardwareRecord(trackType, row, labelOverride = '') {
  if (!row) return false;
  const label = String(labelOverride || buildUnifiedHardwareLabel(row) || trackType || 'Hardware').trim();
  if (trackType === 'motherboard') {
    return window.loadProceduralHardware(
      row.l, row.w, row.h, label, true, `${row.l} x ${row.w} mm board`, 0x38bdf8, 'motherboard',
      {
        catalogId:row.catalogId||row.id||'', formFactor:row.formFactor||'ITX', brand:row.brand||'', model:row.model||'',
        socket:row.socket||'', chipset:row.chipset||'', ramSlots:Number(row.ramSlots)||defaultMotherboardRamSlotCount(row.formFactor||'ITX'), ramType:row.ramType||'',
        dimensionKnown:row.dimensionKnown||null, dimensionSource:row.dimensionSource||'unknown'
      }
    );
  }
  if (trackType === 'psu') {
    return window.loadProceduralHardware(
      row.l, row.w, row.h, label, true, `${row.l} x ${row.w} x ${row.h} mm`, 0xf87171, 'psu',
      {
        catalogId:row.catalogId||row.id||'', formFactor:row.formFactor||'SFX', brand:row.brand||'', model:row.model||'',
        wattage:Number(row.wattage)||0, dimensionKnown:row.dimensionKnown||null,
        dimensionSource:row.dimensionSource||'unknown'
      }
    );
  }
  if (trackType === 'gpu') {
    return window.loadProceduralHardware(
      row.l, row.w, row.h, label, true, `${row.l} x ${row.w} x ${row.h} mm`, 0x38bdf8, 'gpu',
      { catalogId:row.catalogId||row.id||'', dimensionKnown:row.dimensionKnown||null, gpuHeightSource:row.gpuHeightSource||'catalog_width' }
    );
  }
  if (trackType === 'aircooler') {
    const ramText=row.ramClearanceOpen?'RAM no limit':(Number(row.ramClearance)>0?`RAM \u2264 ${Math.round(Number(row.ramClearance)*10)/10} mm`:'RAM clearance unknown');
    return window.loadProceduralHardware(
      row.l, row.w, row.h, label, true, `${row.l} x ${row.w} x ${row.h} mm \u00b7 ${ramText}`, 0xa1a1aa, 'aircooler',
      {
        catalogId:row.catalogId||row.id||'', ramClearance:Number(row.ramClearance)||0, ramClearanceKnown:!!row.ramClearanceKnown,
        ramClearanceOpen:!!row.ramClearanceOpen, coolerType:row.coolerType||'',
        dimensionKnown:row.dimensionKnown||null, dimensionSource:row.dimensionSource||'unknown'
      }
    );
  }
  if (trackType === 'ram') {
    return window.loadProceduralHardware(
      row.l||133, row.w||7, row.h, label, true, `${Math.round(Number(row.h)*100)/100} mm RAM height`, 0xc084fc, 'ram',
      {
        catalogId:row.catalogId||row.id||'', ramHeight:Number(row.ramHeight||row.h)||0, ramHeightKnown:!!row.ramHeightKnown,
        memoryType:row.memoryType||'', rgb:!!row.rgb, ramKitModules:Number(row.ramKitModules)||2,
        dimensionKnown:row.dimensionKnown||null, dimensionSource:row.dimensionSource||'catalog_ram_height_visual_udimm_envelope'
      }
    );
  }
  if (trackType === 'radiator') {
    return window.loadProceduralHardware(
      row.l, row.w, row.h, label, true, `${row.l} x ${row.w} x ${row.h} mm`, 0x007dec, 'radiator',
      {
        catalogId:row.catalogId||row.id||'', radiatorSize:row.radiatorSize||0, fanProfile:row.fanProfile||row.radFan||'standard',
        radFan:row.radFan||row.fanProfile||'standard', fanThickness:Number(row.fanThickness)||25,
        fanSize:Number(row.fanSize)||0, fanCount:Number(row.fanCount)||0,
        aioBlock:row.aioBlock?{...row.aioBlock,dimensionKnown:{...(row.aioBlock.dimensionKnown||{})}}:null,
        tubeLength:Number(row.tubeLength)||350, tubeLengthKnown:!!row.tubeLengthKnown,
        tubeLengthAssumed:!!row.tubeLengthAssumed || !row.tubeLengthKnown, pumpLocation:row.pumpLocation||'',
        dimensionKnown:row.dimensionKnown||null, dimensionSource:row.dimensionSource||'unknown'
      }
    );
  }
  return false;
}

function setSpatialEvidenceAdvisories(comp,candidate) {
  if(!comp)return;
  const out=[];
  const evidence=comp.trackType==='radiator' ? candidate?.sourceSpatialEvidence : null;
  if(evidence){
    const zone=String(evidence.zone||candidate?.mount||'mount').replace(/_/g,' ');
    if(evidence.conditional){
      const conditions=(evidence.conditions||[]).map(value=>String(value).replace(/_/g,' ')).join(', ');
      out.push({
        type:'source_mount_condition',
        message:`Source note describes the ${zone} radiator mount conditionally${conditions?` (${conditions})`:''}.`
      });
    }
    const radThickness=Number(comp.nativeSpecs?.h)||0;
    const fanThickness=Number(comp.componentMeta?.fanThickness)||0;
    const stackThickness=radThickness+(fanThickness||0);
    if(Number(evidence.maxRadiatorThickness)>0 && radThickness>Number(evidence.maxRadiatorThickness)+0.25){
      out.push({
        type:'source_radiator_thickness',
        message:`Source note limits the ${zone} radiator to ${Number(evidence.maxRadiatorThickness)} mm; selected radiator is ~${Math.round(radThickness*10)/10} mm.`
      });
    }
    if(Number(evidence.maxStack)>0 && stackThickness>Number(evidence.maxStack)+0.25){
      out.push({
        type:'source_stack_thickness',
        message:`Source note limits the ${zone} radiator + fan stack to ${Number(evidence.maxStack)} mm; selected assembly is ~${Math.round(stackThickness*10)/10} mm.`
      });
    }
  }
  comp.spatialEvidenceAdvisories=out;
  combinedRuntimeAdvisories(comp);
}

function parkUnplacedComponent(comp,parentChassis,slotIndex=0,placementStatus=null) {
  if(!comp||!parentChassis)return;
  if(comp.parent!==scene)scene.attach(comp);
  const caseBox=calculateCleanBox(parentChassis);
  const compBox=calculateCleanBox(comp);
  const compSize=compBox.getSize(new THREE.Vector3());
  const caseCenter=caseBox.getCenter(new THREE.Vector3());
  const x=caseBox.max.x+compSize.x/2+45+(slotIndex*18);
  const y=Math.max(compSize.y/2,0.5);
  const z=caseCenter.z+(slotIndex*Math.min(55,Math.max(24,compSize.z*0.15)));
  comp.position.set(x,y,z);
  comp.isLockedToCase=false;
  comp.activeSnapId=null;
  comp.activeSnapSource='unplaced_parking';
  if(comp.trackType==='motherboard')comp.motherboardFrameSemantics=null;
  comp.relationalOrientationTrainingTier='none';
  comp.runtimeContainment={status:'unplaced',reason:placementStatus?.reason||'no_hard_valid_candidate',reasons:placementStatus?.reasons||[]};
  comp.spatialEvidenceAdvisories=[];
  combinedRuntimeAdvisories(comp);
  comp.updateMatrixWorld(true);
  if(comp.wireframeHelper)comp.wireframeHelper.update();
  updateComponentLockUi(comp);
  updateComponentPlacementUi(comp);
}

function realignComponentFromBlueprint(comp,bp,parentChassis) {
  if (!comp?.isComponent || comp.trackType === 'fan') return false;
  if(comp.trackType==='ram'){
    const attached=checkAndAttachRamToMotherboard(comp);
    comp.activeSnapId=attached?'motherboard_ram_kit':null;
    comp.activeSnapSource=attached?(comp.componentMeta?.placementSource||'motherboard_ram_slots_reference'):'unplaced';
    comp.runtimeContainment={status:attached?'placed':'unplaced',reason:attached?(comp.componentMeta?.placementSource||'motherboard_ram_slots_reference'):'motherboard_required'};
    updateComponentPlacementUi(comp);
    updateSliderUINodeValues(comp);
    return attached;
  }
  const placementStatus=componentPlacementStatus(bp,comp);
  if(!placementStatus.valid){
    parkUnplacedComponent(comp,parentChassis,activeBoxes.filter(b=>b.isComponent).indexOf(comp),placementStatus);
    updateSliderUINodeValues(comp);
    return false;
  }
  if (comp.isAirCooler) {
    checkAndAttachToMotherboardSocket(comp); comp.activeSnapId='cpu_socket'; comp.activeSnapSource='motherboard_socket';
    comp.runtimeContainment={status:'placed',reason:'motherboard_socket'}; updateComponentPlacementUi(comp); return true;
  }
  const candidate = chooseSnapCandidate(bp, comp);
  if (!candidate) {
    parkUnplacedComponent(comp,parentChassis,activeBoxes.filter(b=>b.isComponent).indexOf(comp),{valid:false,reason:'no_mount_candidate',reasons:['no_mount_candidate']});
    updateSliderUINodeValues(comp);
    return false;
  }
  if (comp.preferredOrientationTopology && comp.preferredOrientationTopology !== bp.topologyId) {
    comp.preferredOrientationClass = null;
    comp.preferredSnapId = null;
    comp.preferredOrientationTopology = null;
  }
  if (comp.isLockedToCase && comp.parent === parentChassis) comp.position.copy(candidate.pos);
  else { if (comp.parent !== scene) scene.attach(comp); comp.position.copy(candidate.pos).add(parentChassis.position); }
  applyOrientationClass(comp, candidate.orientationClass);
  if (Number.isInteger(comp.preferredOrientationClass) && Number(candidate.orientationClass)===Number(comp.preferredOrientationClass)) {
    comp.relationalOrientationSource='user_profile_candidate';
    comp.relationalOrientationTrainingTier='none';
  } else {
    comp.relationalOrientationSource='constraint_solver'; comp.relationalOrientationTrainingTier='weak_layout_prior';
  }
  const containment = reconcileComponentWithinCase(comp,parentChassis,scene,2);
  comp.lastSolverExpectedWorldSize = Array.isArray(candidate.expectedWorldSize) ? [...candidate.expectedWorldSize] : null;
  comp.activeSnapId=candidate.id;
  comp.activeMount=candidate.mount||null;
  comp.activeSnapSource=bp.placementSelectionSource==='relational_gnn_ranker'?'relational_gnn_ranker':'deterministic_layout_template';
  if(comp.trackType==='motherboard'){
    comp.motherboardFrameSemantics={
      authority:candidate.motherboardFrameAuthority||'unknown',
      componentNormalX:Number.isFinite(Number(candidate.componentNormalX))?Number(candidate.componentNormalX):null,
      ioEdge:candidate.ioEdge||null,
      pcieEdge:candidate.pcieEdge||null,
      motherboardChamber:candidate.motherboardChamber||null,
      gpuChamber:candidate.gpuChamber||null,
      source:candidate.frameSource||candidate.chamberSource||null
    };
  }
  if(comp.trackType==='gpu'&&bp.layoutFamily==='reference'){
    const board=activeBoxes.find(item=>item?.isComponent&&item.trackType==='motherboard');
    comp.pcieAttachedToMoboUid=board?.uid||null;
  }else if(comp.trackType==='gpu')comp.pcieAttachedToMoboUid=null;
  comp.updateMatrixWorld(true);
  if(!comp.runtimeContainment?.shifted) comp.runtimeContainment={status:'placed',reason:'hard_valid_candidate'};
  setSpatialEvidenceAdvisories(comp,candidate);
  updateComponentPlacementUi(comp);
  if (comp.wireframeHelper) comp.wireframeHelper.update(); updateSliderUINodeValues(comp);
  return true;
}

function applyBlueprintRealignment(expectedEpoch = null, { policy = 'all', targetUid = null } = {}) {
  if (activeEngineMode !== 'simulator' || !SFF_SimulatorEngine.activeBlueprint) return;
  const bp=SFF_SimulatorEngine.activeBlueprint;
  if (expectedEpoch !== null && Number(bp.solveEpoch) !== Number(expectedEpoch)) return;
  const parentChassis=activeCaseBox();
  if(!parentChassis)return;

  if(policy==='target'){
    if(targetUid) window.realignComponentToActiveBlueprint?.(targetUid,expectedEpoch);
    refreshFanPlacementUi();
    return;
  }

  const preserveManual=policy==='preserve_manual';
  activeBoxes.forEach(comp=>{
    if(!comp?.isComponent || comp.trackType==='fan')return;
    const isTarget=!!targetUid && comp.uid===targetUid;
    if(preserveManual && !isTarget && comp.spatialUserAdjusted){
      // Manual XYZ is user authority until a case/mode transaction explicitly invalidates
      // the coordinate frame. Keep the live mesh exactly where the user left it.
      comp.updateMatrixWorld?.(true);
      comp.wireframeHelper?.update?.();
      updateSliderUINodeValues(comp);
      updateComponentPlacementUi(comp);
      return;
    }
    realignComponentFromBlueprint(comp,bp,parentChassis);
  });

  enforcePlacedCaseFanReservations(parentChassis,{targetUid});
  ensureAutoFabricatedRadiatorFans(bp,parentChassis);
  activeBoxes.filter(item=>item?.trackType==='fan' && !item.autoFabricatedFan && item.attachedToRadiatorUid)
    .forEach(item=>{checkAndSnapFanToRadiator(item,true);updateSliderUINodeValues(item);});
  aioAssemblyManager?.ensure?.();
  refreshFanPlacementUi();
}

window.realignComponentToActiveBlueprint = function(uid, expectedEpoch = null) {
  if (activeEngineMode !== 'simulator' || !SFF_SimulatorEngine.activeBlueprint) return false;
  const bp=SFF_SimulatorEngine.activeBlueprint;
  if (expectedEpoch !== null && Number(bp.solveEpoch) !== Number(expectedEpoch)) return false;
  const parentChassis=activeCaseBox(); if(!parentChassis)return false;
  const comp=activeBoxes.find(item=>item?.uid===uid);
  if(!comp)return false;
  const placed=realignComponentFromBlueprint(comp,bp,parentChassis);
  enforcePlacedCaseFanReservations(parentChassis,{targetUid:uid});
  // If the target is the radiator, its attached fan children remain a single movable stack.
  if(comp.trackType==='radiator'){
    ensureAutoFabricatedRadiatorFans(bp,parentChassis);
    activeBoxes.filter(item=>item?.trackType==='fan' && !item.autoFabricatedFan && item.attachedToRadiatorUid===comp.uid)
      .forEach(item=>{checkAndSnapFanToRadiator(item,true);updateSliderUINodeValues(item);});
  }
  aioAssemblyManager?.ensure?.();
  refreshFanPlacementUi();
  return placed;
};

window.realignAllComponentsToActiveBlueprint = function(expectedEpoch = null) {
  applyBlueprintRealignment(expectedEpoch,{policy:'all'});
};
window.cycleComponentSnap = function(uid, direction = 1) {
  const comp=activeBoxes.find(b=>b.uid===uid); const plan=SFF_SimulatorEngine.activeBlueprint;
  if (!comp || comp.isAirCooler) return;
  if (comp.trackType === 'ram') { window.snapRamToMotherboard?.(uid); return; }
  if (comp.trackType === 'fan') {
    if (comp.autoFabricatedFan) { window.armAutoFanReplacement?.(uid); return; }
    // Fan slots are owned by the radiator assembly rather than the primary component snap map.
    // An explicit Snap click therefore claims the nearest free solver fan slot regardless of
    // spawn distance; dragging still uses proximity snapping.
    const snapped=checkAndSnapFanToRadiator(comp,false,{force:true,allowSizeMismatch:false});
    if(snapped && activeCaseBox())ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,activeCaseBox());
    updateSliderUINodeValues(comp);
    return;
  }
  if (!plan) return;
  // Explicit Snap means THIS component is being handed back to solver authority. Clear
  // its manual-position flag before rebuilding so a user-shifted radiator cannot feed its
  // current position straight back into the solve as a runtime anchor. Other components
  // retain their own manual authority and remain fixed in the live scene.
  const wasUserAdjusted=!!comp.spatialUserAdjusted;
  const hadPreferred=!!comp.preferredSnapId;
  comp.spatialUserAdjusted=false;
  const freshPlan=rebuildSimulatorPlan({
    realign:false,
    reason:'relational_snap',
    realignUid:uid,
    realignPolicy:'target'
  });
  if(!freshPlan){
    comp.spatialUserAdjusted=wasUserAdjusted;
    return;
  }
  // If the component was merely dragged away from an existing preferred snap, Snap is a
  // restore action: keep that preferred candidate. Cycling remains available for an already
  // solver-owned component that has an explicit preferred snap candidate.
  if(!wasUserAdjusted && hadPreferred)cycleSnapCandidate(freshPlan,comp,direction);
  else if(!hadPreferred)comp.preferredSnapId=null;
  // Immediate deterministic placement is target-only. The same `target` policy is carried
  // into the asynchronous GNN rerank by rebuildSimulatorPlan(), preventing a later advisory
  // result from realigning the rest of the build. Radiator fan children follow only their
  // snapped radiator as one local assembly.
  window.realignComponentToActiveBlueprint(uid,freshPlan.solveEpoch);
};

window.snapRamToMotherboard = function(uid) {
  const ram=activeBoxes.find(item=>item?.uid===uid&&item.trackType==='ram');
  if(!ram)return false;
  const attached=checkAndAttachRamToMotherboard(ram);
  if(!attached){
    ram.runtimeContainment={status:'unplaced',reason:'motherboard_required'};
    updateComponentPlacementUi(ram);
    return false;
  }
  ram.spatialUserAdjusted=false;
  ram.runtimeContainment={status:'placed',reason:ram.componentMeta?.placementSource||'motherboard_ram_slots_reference'};
  updateComponentPlacementUi(ram);
  updateSliderUINodeValues(ram);
  workbenchRuntime?.markSpatialDirty?.();
  return true;
};

window.switchToolWorkspaceMode = function(targetMode) {
    if (activeEngineMode === targetMode) return;
    activeEngineMode = targetMode;

    document.querySelectorAll('.sff-mode-tab').forEach(btn => btn.classList.remove('active'));
    document.getElementById(`mode-btn-${targetMode}`).classList.add('active');

    const manifestTitleEl = document.querySelector('.sff-manifest-title');
    if (manifestTitleEl) manifestTitleEl.textContent = targetMode === 'simulator' ? 'ACTIVE SIMULATION' : 'ACTIVE WORKBENCH';
    getNearFitController()?.setMode?.(activeEngineMode);

    if (activeEngineMode === 'simulator') {
        const existingCases = activeBoxes.filter(b => b.isCase);
        if (existingCases.length > 1) {
            for (let i = 1; i < existingCases.length; i++) {
                window.removeModelInstance(existingCases[i].uid);
            }
        }
        
        const targetCase = activeBoxes.find(b => b.isCase);
        if (targetCase) {
            targetCase.position.set(0, 0, 120);
            targetCase.updateMatrixWorld(true);
            const cleanBox = calculateCleanBox(targetCase);
            targetCase.position.y = -cleanBox.min.y + (targetCase.addedFeetHeight || 0);
            targetCase.updateMatrixWorld(true);
            if(targetCase.wireframeHelper) targetCase.wireframeHelper.update();
            
            rebuildSimulatorPlan({ realign: true, reason: 'workspace_mode' });
        }
        centerCameraOnActiveCase();
    } else {
        activeBoxes.forEach(b => {
            if (b.isComponent && b.isLockedToCase) {
                if(!(b.trackType==='fan'&&b.attachedToRadiatorUid&&!b.fanAssemblyDetached))scene.attach(b);
                b.isLockedToCase = false;
            }
        });
        SFF_SimulatorEngine.clearClearanceHelpers();
        SFF_SimulatorEngine.activeBlueprint = null;
    }
};

window.toggleComponentLock = function(uid, btnElement) {
    const comp = activeBoxes.find(b => b.uid === uid);
    const targetCase = activeBoxes.find(b => b.isCase);
    if (!comp || !targetCase) return;

    // Snapped fans belong to the radiator stack by default. Their lock control becomes the
    // explicit "edit this fan independently" switch instead of breaking the radiator lock.
    if(comp.trackType==='fan' && comp.attachedToRadiatorUid){
        const radiator=activeBoxes.find(item=>item.uid===comp.attachedToRadiatorUid&&item.trackType==='radiator');
        if(!radiator)return;
        comp.fanAssemblyDetached=!comp.fanAssemblyDetached;
        if(comp.fanAssemblyDetached){
            scene.attach(comp);
            comp.isLockedToCase=false;
        }else{
            radiator.attach(comp);
            comp.isLockedToCase=!!radiator.isLockedToCase;
        }
        if(btnElement){
            btnElement.innerHTML=comp.fanAssemblyDetached?`${SVG_LOCK_OPEN} <span>Fan Free</span>`:`${SVG_LOCK_CLOSED} <span>Stack</span>`;
            btnElement.style.color=comp.fanAssemblyDetached?'var(--sub-text)':'#38bdf8';
        }
        updateSliderUINodeValues(comp);
        return;
    }

    comp.isLockedToCase = !comp.isLockedToCase;
    if (comp.isLockedToCase) targetCase.attach(comp);
    else {
        scene.attach(comp);
        if(comp.trackType==='fan' && comp.attachedToCaseUid){
            comp.attachedToCaseUid=null;
            comp.caseFanMountId=null;
            comp.caseFanMountSlot=null;comp.caseFanMountCount=null;comp.caseFanMountFrame=null;
            comp.caseFanMountZone=null;
            comp.caseFanMountFace=null;
            comp.activeFanSnapId=null;
        }
    }

    if(btnElement){
        btnElement.innerHTML=comp.isLockedToCase?`${SVG_LOCK_CLOSED} <span>Locked</span>`:`${SVG_LOCK_OPEN} <span>Unlocked</span>`;
        btnElement.style.color=comp.isLockedToCase?'#38bdf8':'var(--sub-text)';
    }

    // The radiator owns its snapped fan hierarchy. Locking/unlocking the radiator changes
    // the whole assembly state without re-parenting individual fans away from it.
    if(comp.trackType==='radiator'){
        activeBoxes.filter(fan=>fan?.trackType==='fan'&&fan.attachedToRadiatorUid===comp.uid&&!fan.fanAssemblyDetached).forEach(fan=>{
            if(fan.parent!==comp)comp.attach(fan);
            fan.isLockedToCase=comp.isLockedToCase;
            const fanBtn=document.getElementById(`lock_btn_${fan.uid}`);
            if(fanBtn){fanBtn.innerHTML=`${SVG_LOCK_CLOSED} <span>Stack</span>`;fanBtn.style.color='#38bdf8';}
        });
    }

    if(!comp.isLockedToCase){
        const masterBtn=document.querySelector(`[id^="master_lock_btn_"]`);
        if(masterBtn){
            masterBtn.innerHTML=`${SVG_LOCK_OPEN} <span>Lock All</span>`;
            masterBtn.style.color='var(--sub-text)';
            targetCase.isMasterLocked=false;
        }
    }
    if(comp.wireframeHelper)comp.wireframeHelper.update();
    if(comp.trackType==='fan')manifestUi?.updateFanMountSummary?.(comp);
    refreshFanPlacementUi();
};

window.toggleCaseMasterLock = function(caseUid, btnElement) {
    const targetCase = activeBoxes.find(b => b.uid === caseUid);
    if (!targetCase) return;

    targetCase.isMasterLocked = !targetCase.isMasterLocked;
    const components = activeBoxes.filter(b => b.isComponent);

    // Parent radiators/primary components first; linked fans stay underneath their radiator.
    components.filter(comp=>comp.trackType!=='ram'&&!(comp.trackType==='fan'&&comp.attachedToRadiatorUid&&!comp.fanAssemblyDetached)).forEach(comp=>{
        comp.isLockedToCase=targetCase.isMasterLocked;
        if(targetCase.isMasterLocked)targetCase.attach(comp);
        else {
            scene.attach(comp);
            if(comp.trackType==='fan' && comp.attachedToCaseUid){
                comp.attachedToCaseUid=null;
                comp.caseFanMountId=null;
                comp.caseFanMountSlot=null;comp.caseFanMountCount=null;comp.caseFanMountFrame=null;
                comp.caseFanMountZone=null;
                comp.caseFanMountFace=null;
                comp.activeFanSnapId=null;
            }
        }
    });
    components.filter(comp=>comp.trackType==='fan'&&comp.attachedToRadiatorUid&&!comp.fanAssemblyDetached).forEach(fan=>{
        const radiator=activeBoxes.find(item=>item.uid===fan.attachedToRadiatorUid);
        if(radiator&&fan.parent!==radiator)radiator.attach(fan);
        fan.isLockedToCase=!!radiator?.isLockedToCase;
    });
    components.filter(comp=>comp.trackType==='ram').forEach(ram=>checkAndAttachRamToMotherboard(ram));

    components.forEach(comp=>{
        const compBtn=document.getElementById(`lock_btn_${comp.uid}`);
        if(!compBtn)return;
        if(comp.trackType==='ram'){
            return;
        }else if(comp.trackType==='fan'&&comp.attachedToRadiatorUid&&!comp.fanAssemblyDetached){
            compBtn.innerHTML=`${SVG_LOCK_CLOSED} <span>Stack</span>`;
            compBtn.style.color='#38bdf8';
        }else{
            compBtn.innerHTML=targetCase.isMasterLocked?`${SVG_LOCK_CLOSED} <span>Locked</span>`:`${SVG_LOCK_OPEN} <span>Unlocked</span>`;
            compBtn.style.color=targetCase.isMasterLocked?'#38bdf8':'var(--sub-text)';
        }
        if(comp.wireframeHelper)comp.wireframeHelper.update();
    });

    if (btnElement) {
        btnElement.innerHTML = targetCase.isMasterLocked
            ? `${SVG_LOCK_CLOSED} <span>Unlock All</span>`
            : `${SVG_LOCK_OPEN} <span>Lock All</span>`;
        btnElement.style.color = targetCase.isMasterLocked ? '#38bdf8' : 'var(--sub-text)';
    }
    components.filter(comp=>comp.trackType==='fan').forEach(comp=>manifestUi?.updateFanMountSummary?.(comp));
    refreshFanPlacementUi();
};


// Case-level recovery action. Unlike individual Snap, this deliberately relinquishes manual
// XYZ authority for every solver-backed component and rebuilds the complete CURRENT topology.
// It does not erase preferred snap/orientation choices and it does not change lock state.
window.snapAllComponentsToCase = function(caseUid) {
    const targetCase=activeBoxes.find(b=>b?.isCase&&b.uid===caseUid);
    if(!targetCase || activeEngineMode!=='simulator')return false;

    const manualState=new Map();
    activeBoxes.forEach(comp=>{
        if(!comp?.isComponent)return;
        manualState.set(comp.uid,!!comp.spatialUserAdjusted);
        comp.spatialUserAdjusted=false;
    });

    const freshPlan=rebuildSimulatorPlan({realign:true,reason:'case_snap_all',realignPolicy:'all'});
    if(!freshPlan){
        activeBoxes.forEach(comp=>{
            if(comp?.isComponent&&manualState.has(comp.uid))comp.spatialUserAdjusted=manualState.get(comp.uid);
        });
        return false;
    }

    // Loose/manual fans do not participate in the primary component solve. If a radiator slot
    // exists, reclaim it locally; otherwise leave the fan untouched rather than inventing a case
    // fan mount that the deterministic engine does not model.
    activeBoxes.filter(comp=>comp?.trackType==='fan'&&!comp.autoFabricatedFan).forEach(fan=>{
        if(fan.attachedToRadiatorUid)checkAndSnapFanToRadiator(fan,true,{force:true});
    });
    workbenchRuntime?.markSpatialDirty?.();
    return true;
};

function centerCameraOnActiveCase() {
    const targetCase = activeBoxes.find(b => b.isCase);
    if (targetCase && controls) {
        controls.target.copy(targetCase.position);
        controls.update();
        
        const xCamSlider = document.getElementById('cam-target-x');
        const yCamSlider = document.getElementById('cam-target-y');
        if (xCamSlider) xCamSlider.value = targetCase.position.x;
        if (yCamSlider) yCamSlider.value = targetCase.position.y;
    }
}


function getNextComponentSpawnPosition(modelHeight) {
    const targetCase = activeBoxes.find(b => b.isCase);
    if (activeEngineMode === 'simulator' && targetCase) {
        return new THREE.Vector3(targetCase.position.x, modelHeight / 2, targetCase.position.z);
    }
    const placedComponents = activeBoxes.filter(b => b.isComponent);
    return new THREE.Vector3(-100 + (placedComponents.length * 90), modelHeight / 2, 180);
}

function checkAndAttachToMotherboardSocket(coolerMesh) {
    const targetMobo = activeBoxes.find(b => b.isComponent && b.uid && b.uid.toString().includes('mobo'));
    if (!targetMobo) { coolerMesh.attachedToMoboUid = null; return false; }
    if (coolerMesh.parent !== scene && !coolerMesh.isLockedToCase) scene.add(coolerMesh);
    
    coolerMesh.attachedToMoboUid = targetMobo.uid;
    const ff = String(targetMobo.componentMeta?.formFactor || 'ITX').toUpperCase();
    const socket = socketLandmarkLocal(targetMobo.nativeSpecs || {}, ff);
    let localSocket = new THREE.Vector3(
        socket[0] + (coolerMesh.userOffsetX || 0), socket[1] + (coolerMesh.userOffsetY || 0), socket[2] + (coolerMesh.userOffsetZ || 0)
    );
    
    targetMobo.updateMatrixWorld(true); localSocket.applyMatrix4(targetMobo.matrixWorld);
    
    if (coolerMesh.isLockedToCase && coolerMesh.parent) {
        coolerMesh.parent.worldToLocal(localSocket);
        coolerMesh.position.copy(localSocket);
        
        const worldQuaternion = new THREE.Quaternion();
        targetMobo.getWorldQuaternion(worldQuaternion);
        worldQuaternion.multiply(coolerRelativeQuaternion(coolerMesh.socketQuarterTurn || 0));
        coolerMesh.quaternion.copy(worldQuaternion);
        let parentWorldQuat = new THREE.Quaternion();
        coolerMesh.parent.getWorldQuaternion(parentWorldQuat);
        coolerMesh.quaternion.premultiply(parentWorldQuat.invert());
    } else {
        coolerMesh.position.copy(localSocket);
    targetMobo.getWorldQuaternion(coolerMesh.quaternion);
    coolerMesh.quaternion.multiply(coolerRelativeQuaternion(coolerMesh.socketQuarterTurn || 0));
    }
    coolerMesh.relationalOrientationClass = orientationClassFromObject3D(coolerMesh);
    coolerMesh.relationalOrientationSource = targetMobo.relationalOrientationSource || 'motherboard_attachment';
    coolerMesh.relationalOrientationTrainingTier = targetMobo.relationalOrientationTrainingTier || 'none';
    coolerMesh.updateMatrixWorld(true);
    return true;
}

function proposePositionAxis(comp, axis, proposedVal, bypassForce) {
    const oldVal = comp.position[axis];
    if (bypassForce) {
        comp.position[axis] = proposedVal; comp.spatialUserAdjusted=!!comp.isComponent; comp.updateMatrixWorld(true);
        updateAttachedMoboChildren(comp); return;
    }
    comp.position[axis] = proposedVal; comp.updateMatrixWorld(true);
    const cleanCompBox = calculateCleanBox(comp);
    let isCollidingSameType = false;

    for (let i = 0; i < activeBoxes.length; i++) {
        const obstacle = activeBoxes[i];
        if (obstacle.uid === comp.uid || comp.isComponent !== obstacle.isComponent) continue; 
        if (comp.attachedToMoboUid === obstacle.uid || obstacle.attachedToMoboUid === comp.uid) continue;
        if (comp.pcieAttachedToMoboUid === obstacle.uid || obstacle.pcieAttachedToMoboUid === comp.uid) continue;
        if (comp.attachedToRadiatorUid === obstacle.uid || obstacle.attachedToRadiatorUid === comp.uid) continue;
        const pair=new Set([comp.trackType,obstacle.trackType]);
        if(pair.has('motherboard')&&pair.has('gpu')&&SFF_SimulatorEngine?.activeBlueprint?.layoutFamily==='reference')continue;
        const ramPair=ramCoolerPairCompatibility(comp,obstacle);
        if(ramPair?.allowed)continue;
        if (cleanCompBox.clone().expandByScalar(-0.2).intersectsBox(calculateCleanBox(obstacle).clone().expandByScalar(-0.2))) {
            isCollidingSameType = true; break;
        }
    }
    if (isCollidingSameType) { comp.position[axis] = oldVal + ((proposedVal - oldVal) * 0.15); comp.updateMatrixWorld(true); }
    if (comp.position[axis] !== oldVal) {
        comp.spatialUserAdjusted=!!comp.isComponent;
        updateAttachedMoboChildren(comp);
    }
}

function configureRamKitVisual(ramMesh,placement){
    if(!ramMesh||!placement)return;
    const meshes=Array.isArray(ramMesh.ramModuleMeshes)&&ramMesh.ramModuleMeshes.length
      ? ramMesh.ramModuleMeshes
      : (()=>{const out=[];ramMesh.traverse?.(node=>{if(node?.userData?.sffRamModuleVisual)out.push(node);});return out.sort((a,b)=>(a.userData?.ramModuleIndex||0)-(b.userData?.ramModuleIndex||0));})();
    const moduleH=Number(ramMesh.nativeSpecs?.h)||Number(ramMesh.componentMeta?.ramHeight)||35;
    meshes.forEach((mesh,index)=>{
      const offset=placement.offsets[index]||null;
      mesh.visible=!!offset;
      if(offset)mesh.position.set(Number(offset.x)||0,moduleH/2,Number(offset.z)||0);
    });
    ramMesh.ramModuleMeshes=meshes;
    ramMesh.ramKitModules=placement.moduleCount;
}

function checkAndAttachRamToMotherboard(ramMesh) {
    const targetMobo = activeBoxes.find(b => b.isComponent && b.trackType === 'motherboard');
    if (!targetMobo) {
      ramMesh.attachedToMoboUid = null;
      ramMesh.activeSnapId=null;
      ramMesh.activeSnapSource='motherboard_required';
      if(ramMesh.parent&&ramMesh.parent!==scene)scene.attach(ramMesh);
      manifestUi?.updateRamMountSummary?.(ramMesh);
      return false;
    }
    const ff = normalizeMotherboardFormFactor(
      targetMobo.componentMeta?.formFactor ||
      inferComponentMeta('motherboard', targetMobo.displayLabel || '', targetMobo.nativeSpecs).formFactor ||
      'ITX'
    );
    const slotCount=normalizeRamSlotCount(targetMobo.componentMeta?.ramSlots,defaultMotherboardRamSlotCount(ff));
    const requestedModules=Math.max(1,Math.round(Number(ramMesh.componentMeta?.ramKitModules)||2));
    const placement=ramKitBoardPlacement(targetMobo.nativeSpecs||{},ff,slotCount,requestedModules);
    if(!placement)return false;

    // RAM is a relational child of the motherboard rather than a loose world-space box. This
    // makes it follow board movement, case locking and case transactions automatically.
    if(ramMesh.parent!==targetMobo)targetMobo.add(ramMesh);
    ramMesh.position.set(placement.anchor.x,placement.anchor.y,placement.anchor.z);
    ramMesh.quaternion.identity();
    ramMesh.isLockedToCase=false;
    ramMesh.attachedToMoboUid = targetMobo.uid;
    ramMesh.activeSnapId='motherboard_ram_kit';
    ramMesh.activeSnapSource=placement.source||'motherboard_ram_slots_reference';
    ramMesh.componentMeta = {
      ...(ramMesh.componentMeta||{}),
      ramKitModules:placement.moduleCount,
      motherboardRamSlots:placement.slotCount,
      installedRamSlots:placement.indices.map(index=>index+1),
      placementSource:placement.source||'motherboard_ram_slots_reference'
    };
    configureRamKitVisual(ramMesh,placement);
    ramMesh.relationalOrientationClass = orientationClassFromObject3D(ramMesh);
    ramMesh.relationalOrientationSource = placement.source||'motherboard_ram_slots_reference';
    ramMesh.relationalOrientationTrainingTier = 'none';
    ramMesh.runtimeContainment={status:'placed',reason:placement.source||'motherboard_ram_slots_reference'};
    ramMesh.updateMatrixWorld(true);
    ramMesh.wireframeHelper?.update?.();
    manifestUi?.updateRamMountSummary?.(ramMesh);
    return true;
}

function updateAttachedMoboChildren(comp) {
    if (comp.uid && comp.uid.toString().includes('mobo')) {
        activeBoxes.forEach(child => {
          if (child.isAirCooler) { checkAndAttachToMotherboardSocket(child); updateSliderUINodeValues(child); }
          else if (child.trackType==='ram') { checkAndAttachRamToMotherboard(child); updateSliderUINodeValues(child); }
        });
    }
    if (comp.trackType === 'radiator') {
        // Snapped fans are transform children of the radiator and already move with it. Do
        // not re-snap them to the stale solver coordinates while the user is deliberately
        // shifting the complete AIO stack; just refresh their world-coordinate UI.
        activeBoxes.forEach(child => {
            if (child.trackType === 'fan' && child.attachedToRadiatorUid === comp.uid && !child.fanAssemblyDetached) {
                child.updateMatrixWorld(true);
                if(child.wireframeHelper)child.wireframeHelper.update();
                updateSliderUINodeValues(child);
            }
        });
    }
}


function activeRadiatorFanPlan(radiator) {
    if (!radiator || activeEngineMode !== 'simulator') return [];
    const bp=SFF_SimulatorEngine?.activeBlueprint;
    const candidates=bp?.candidates?.radiator || [];
    const candidate=candidates.find(item=>item.id===radiator.activeSnapId) || candidates[0] || null;
    return Array.isArray(candidate?.autoFanPlan) ? candidate.autoFanPlan : [];
}

function removeAutoFabricatedRadiatorFans(radiatorUid = null, slotIndex = null) {
    const stale=[...activeBoxes].filter(item=>
      item?.trackType==='fan' &&
      item?.autoFabricatedFan &&
      (!radiatorUid || item.attachedToRadiatorUid===radiatorUid) &&
      (slotIndex===null || Number(item.assignedSlotIndex)===Number(slotIndex))
    );
    stale.forEach(item=>window.removeModelInstance?.(item.uid,{suppressRebuild:true,internalTransaction:true}));
}

function radiatorManualFanSlots(radiatorUid, excludeUid = null) {
    return new Set(
      activeBoxes
        .filter(item=>
          item?.trackType==='fan' &&
          !item.autoFabricatedFan &&
          item.uid!==excludeUid &&
          item.attachedToRadiatorUid===radiatorUid &&
          item.assignedSlotIndex !== null &&
          item.assignedSlotIndex !== undefined &&
          Number.isInteger(Number(item.assignedSlotIndex))
        )
        .map(item=>Number(item.assignedSlotIndex))
    );
}

function radiatorOccupiedFanSlots(radiatorUid, excludeUid = null) {
    return new Set(
      activeBoxes
        .filter(item=>
          item?.trackType==='fan' &&
          item.uid!==excludeUid &&
          item.attachedToRadiatorUid===radiatorUid &&
          item.assignedSlotIndex !== null &&
          item.assignedSlotIndex !== undefined &&
          Number.isInteger(Number(item.assignedSlotIndex))
        )
        .map(item=>Number(item.assignedSlotIndex))
    );
}

function fanPlanFrame(planItem) {
    return Number(planItem?.dims?.l || planItem?.dims?.w || 0) || 0;
}

function fanMeshFrame(fanMesh) {
    return Number(fanMesh?.nativeSpecs?.l || fanMesh?.nativeSpecs?.w || 0) || 0;
}

function fanPlanShapeMatches(fanMesh, planItem) {
    const frame=fanPlanFrame(planItem);
    const thickness=Number(planItem?.dims?.h || 0) || 0;
    const actualFrame=fanMeshFrame(fanMesh);
    const actualThickness=Number(fanMesh?.nativeSpecs?.h || 0) || 0;
    return Math.abs(frame-actualFrame)<=0.5 && Math.abs(thickness-actualThickness)<=0.5;
}

function placeFanFromSolverPlan(fanMesh, radiator, planItem, parentChassis) {
    if(!fanMesh||!radiator||!planItem||!parentChassis)return false;
    if(fanMesh.parent!==scene)scene.attach(fanMesh);

    const target=new THREE.Vector3(...planItem.position);
    // A user replacement may use a different thickness from the catalog placeholder.
    // Keep the radiator-facing surface flush by shifting only half the thickness delta along
    // the already-solved radiator normal. Auto placeholders use the exact solver position.
    if(!fanMesh.autoFabricatedFan){
      const plannedThickness=Number(planItem?.dims?.h || 0) || 0;
      const actualThickness=Number(fanMesh?.nativeSpecs?.h || plannedThickness) || plannedThickness;
      const deltaThickness=actualThickness-plannedThickness;
      if(Math.abs(deltaThickness)>0.01){
        const radiatorWorld=radiator.getWorldPosition(new THREE.Vector3());
        const radiatorLocal=radiatorWorld.sub(parentChassis.position);
        const fromRadiator=target.clone().sub(radiatorLocal);
        // Radiator local +Y is its thickness/stack normal. Use the orientation matrix column
        // for that axis instead of the centre-to-fan vector: end fans have a much larger
        // tangential segment offset, which previously made 15 mm replacements slide along
        // the radiator length rather than move flush toward the radiator face.
        const matrix=orientationMatrixForClass(Number(planItem.orientationClass)||0);
        const normal=new THREE.Vector3(matrix[1],matrix[4],matrix[7]).normalize();
        const sign=Math.sign(fromRadiator.dot(normal)) || 1;
        target.addScaledVector(normal,sign*(deltaThickness/2));
      }
    }

    fanMesh.position.copy(target).add(parentChassis.position);
    applyOrientationClass(fanMesh,Number(planItem.orientationClass)||0);
    fanMesh.attachedToCaseUid=null;
    fanMesh.caseFanMountId=null;
    fanMesh.caseFanMountSlot=null;fanMesh.caseFanMountCount=null;fanMesh.caseFanMountFrame=null;
    fanMesh.caseFanMountZone=null;
    fanMesh.caseFanMountFace=null;
    fanMesh.attachedToRadiatorUid=radiator.uid;
    fanMesh.assignedSlotIndex=Number(planItem.index)||0;
    fanMesh.activeFanSnapId=`radfan:${radiator.uid}:${Number(planItem.index)||0}`;
    fanMesh.fanAssemblyDetached=false;
    fanMesh.relationalOrientationSource=fanMesh.autoFabricatedFan?'radiator_solver_stack':'radiator_user_replacement';
    fanMesh.relationalOrientationTrainingTier='none';
    fanMesh.runtimeContainment={status:'placed',reason:fanMesh.autoFabricatedFan?'radiator_solver_stack':'radiator_user_replacement'};
    fanMesh.updateMatrixWorld(true);
    // A snapped fan is part of the radiator assembly by default. Object3D.attach preserves
    // the solved world transform while making subsequent radiator movement/locking carry the
    // fan automatically. The fan can still be explicitly detached for individual editing.
    radiator.attach(fanMesh);
    fanMesh.isLockedToCase=!!radiator.isLockedToCase;
    fanMesh.updateMatrixWorld(true);
    if(fanMesh.wireframeHelper)fanMesh.wireframeHelper.update();
    const lockBtn=document.getElementById(`lock_btn_${fanMesh.uid}`);
    if(lockBtn){lockBtn.innerHTML=`${SVG_LOCK_CLOSED} <span>Stack</span>`;lockBtn.style.color='#38bdf8';}
    manifestUi?.updateFanMountSummary?.(fanMesh);
    return true;
}

function createAutoFabricatedRadiatorFan(radiator, planItem, parentChassis) {
    const frame=Number(planItem?.dims?.l || planItem?.dims?.w || radiator?.componentMeta?.fanSize || 120) || 120;
    const thickness=Number(planItem?.dims?.h || radiator?.componentMeta?.fanThickness || 25) || 25;
    const label=`Auto Fan ${Number(planItem?.index||0)+1} (${frame}x${thickness}mm)`;
    const fan=createProceduralComponent({trackType:'fan',l:frame,w:frame,h:thickness,label,color:0xa78bfa,isComponent:true});
    fan.displayLabel=label;
    fan.autoFabricatedFan=true;
    fan.autoFabricatedSource='aio_cooling_assembly';
    fan.uid=`sff_auto_fan_${radiator.uid}_${Number(planItem?.index||0)}_${Date.now()}_${Math.random().toString(36).slice(2,5)}`;
    scene.add(fan);
    activeBoxes.push(fan);
    placeFanFromSolverPlan(fan,radiator,planItem,parentChassis);
    const assignedColor=fan.categoryColor||0xa78bfa;
    const cageHelper=new THREE.BoxHelper(fan,assignedColor);scene.add(cageHelper);fan.wireframeHelper=cageHelper;
    manifestUi?.attachHtmlBadgeNode?.(fan,label);
    manifestUi?.appendDomManifestRow?.(fan,label,`${frame} x ${thickness} x ${frame} mm`,assignedColor,true);
    const fanLockBtn=document.getElementById(`lock_btn_${fan.uid}`);
    if(fanLockBtn){fanLockBtn.innerHTML=`${SVG_LOCK_CLOSED} <span>Stack</span>`;fanLockBtn.style.color='#38bdf8';}
    updateSliderUINodeValues(fan);
    return fan;
}

function ensureAutoFabricatedRadiatorFans(plan,parentChassis) {
    const radiators=activeBoxes.filter(item=>item?.isComponent && item.trackType==='radiator');
    if(!radiators.length){removeAutoFabricatedRadiatorFans();return;}
    const liveUids=new Set(radiators.map(rad=>rad.uid));
    [...activeBoxes].filter(item=>item?.autoFabricatedFan && !liveUids.has(item.attachedToRadiatorUid))
      .forEach(item=>window.removeModelInstance?.(item.uid,{suppressRebuild:true,internalTransaction:true}));

    for(const radiator of radiators){
      const fanPlan=activeRadiatorFanPlan(radiator);
      if(!fanPlan.length){removeAutoFabricatedRadiatorFans(radiator.uid);continue;}

      const manualSlots=radiatorManualFanSlots(radiator.uid);
      const planBySlot=new Map(fanPlan.map(item=>[Number(item.index)||0,item]));
      const existingAutos=[...activeBoxes].filter(item=>item?.trackType==='fan' && item?.autoFabricatedFan && item.attachedToRadiatorUid===radiator.uid);

      // Remove stale/duplicate placeholders, but keep a correctly-shaped placeholder in place.
      const keptSlots=new Set();
      for(const fan of existingAutos){
        const slot=Number(fan.assignedSlotIndex)||0;
        const planItem=planBySlot.get(slot);
        if(!planItem || manualSlots.has(slot) || keptSlots.has(slot) || !fanPlanShapeMatches(fan,planItem)){
          window.removeModelInstance?.(fan.uid,{suppressRebuild:true,internalTransaction:true});
          continue;
        }
        keptSlots.add(slot);
        placeFanFromSolverPlan(fan,radiator,planItem,parentChassis);
        updateSliderUINodeValues(fan);
      }

      // A user-created fan owns its solver slot. Only materialise placeholders for empty slots.
      for(const planItem of fanPlan){
        const slot=Number(planItem.index)||0;
        if(manualSlots.has(slot) || keptSlots.has(slot))continue;
        createAutoFabricatedRadiatorFan(radiator,planItem,parentChassis);
        keptSlots.add(slot);
      }
    }
    manifestUi?.checkEmptyState?.();
}

function solverFanSlotTargets(fanMesh, { allowSizeMismatch = false } = {}) {
    const parentChassis=activeCaseBox();
    if(!parentChassis)return[];
    const fanWorldPos=new THREE.Vector3(); fanMesh.getWorldPosition(fanWorldPos);
    const manualFrame=fanMeshFrame(fanMesh);
    const targets=[];
    for(const radiator of activeBoxes.filter(item=>item?.trackType==='radiator')){
      const plan=activeRadiatorFanPlan(radiator);
      if(!plan.length)continue;
      const occupiedSlots=radiatorOccupiedFanSlots(radiator.uid,fanMesh.uid);
      for(let index=0;index<plan.length;index++){
        const item=plan[index];
        const slot=Number(item?.index ?? index);
        if(occupiedSlots.has(slot))continue;
        const frame=fanPlanFrame(item);
        if(!allowSizeMismatch && manualFrame && frame && Math.abs(frame-manualFrame)>4)continue;
        const targetWorld=new THREE.Vector3(...item.position).add(parentChassis.position);
        targets.push({radiator,item:{...item,index:slot},slot,frame,targetWorld,distance:targetWorld.distanceTo(fanWorldPos)});
      }
    }
    return targets.sort((a,b)=>a.distance-b.distance);
}


function fanPlacementPreview(frame,thickness) {
    const preview=new THREE.Object3D();
    preview.uid='__sff_fan_preview__';
    preview.trackType='fan';
    preview.isComponent=true;
    preview.nativeSpecs={l:frame,w:frame,h:thickness};
    const box=activeCaseBox();
    if(box)preview.position.copy(box.getWorldPosition(new THREE.Vector3()));
    preview.updateMatrixWorld(true);
    return preview;
}

function fanReflowOwner(item) {
    if(!item)return null;
    if(['motherboard','gpu','psu'].includes(item.trackType))return item;
    if(item.attachedToMoboUid)return activeBoxes.find(x=>x?.uid===item.attachedToMoboUid&&x.trackType==='motherboard')||null;
    return null;
}

function fanReflowTargetOwners(target) {
    const owners=[...new Set((target?.blockers||[]).map(fanReflowOwner).filter(Boolean))];
    // Bounded relational search: one or two solver-owned blockers may move together. This is
    // enough for the common vertical-sandwich top-fan case where both motherboard and GPU
    // occupy the declared fan plane, without turning fan placement into an unbounded solver.
    if(!owners.length||owners.length>2)return[];
    for(const owner of owners){
      if(owner.spatialUserAdjusted || owner.isLockedToCase)return[];
      const candidates=SFF_SimulatorEngine?.activeBlueprint?.candidates?.[owner.trackType]||[];
      if(!candidates.length)return[];
    }
    return owners;
}

function componentSafeAfterFanReflow(comp,parentChassis) {
    if(!comp||!parentChassis)return false;
    comp.updateMatrixWorld(true);parentChassis.updateMatrixWorld(true);
    const own=calculateCleanBox(comp).clone().expandByScalar(-0.2);
    const inside=calculateCleanBox(parentChassis).clone().expandByScalar(-1.5);
    if(!inside.containsBox(own))return false;
    for(const obstacle of activeBoxes){
      if(!obstacle?.isComponent||obstacle===comp||obstacle.uid===comp.uid)continue;
      if(obstacle.runtimeContainment?.status==='parked')continue;
      if(comp.attachedToMoboUid===obstacle.uid||obstacle.attachedToMoboUid===comp.uid)continue;
      if(comp.pcieAttachedToMoboUid===obstacle.uid||obstacle.pcieAttachedToMoboUid===comp.uid)continue;
      if(comp.attachedToRadiatorUid===obstacle.uid||obstacle.attachedToRadiatorUid===comp.uid)continue;
      const ramPair=ramCoolerPairCompatibility(comp,obstacle);
      if(ramPair?.allowed)continue;
      const pair=new Set([comp.trackType,obstacle.trackType]);
      if(pair.has('motherboard')&&pair.has('gpu')&&SFF_SimulatorEngine?.activeBlueprint?.layoutFamily==='reference')continue;
      if(own.intersectsBox(calculateCleanBox(obstacle).clone().expandByScalar(-0.2)))return false;
    }
    return true;
}

function applyFanReflowCandidate(comp,candidate,parentChassis){
    if(!comp||!candidate||!parentChassis)return false;
    if(comp.parent!==scene)scene.attach(comp);
    comp.position.copy(candidate.pos).add(parentChassis.position);
    applyOrientationClass(comp,Number(candidate.orientationClass)||0);
    comp.updateMatrixWorld(true);
    updateAttachedMoboChildren(comp);
    return true;
}

function fanReflowCandidateList(owner){
    const all=[...(SFF_SimulatorEngine?.activeBlueprint?.candidates?.[owner.trackType]||[])]
      .sort((a,b)=>(Number(b.priority)||0)-(Number(a.priority)||0));
    const current=all.find(candidate=>candidate?.id===owner.activeSnapId)||null;
    const alternatives=all.filter(candidate=>candidate!==current).slice(0,5);
    return current?[current,...alternatives]:alternatives.slice(0,6);
}

function savedReflowState(owner){
    return {
      parent:owner.parent,position:owner.position.clone(),quaternion:owner.quaternion.clone(),scale:owner.scale.clone(),
      activeSnapId:owner.activeSnapId,activeMount:owner.activeMount,activeSnapSource:owner.activeSnapSource,
      preferredSnapId:owner.preferredSnapId,spatialUserAdjusted:owner.spatialUserAdjusted,runtimeContainment:owner.runtimeContainment
    };
}

function restoreReflowState(owner,saved){
    if(saved.parent) saved.parent.add(owner); else scene.add(owner);
    owner.position.copy(saved.position);owner.quaternion.copy(saved.quaternion);owner.scale.copy(saved.scale);
    owner.activeSnapId=saved.activeSnapId;owner.activeMount=saved.activeMount;owner.activeSnapSource=saved.activeSnapSource;
    owner.preferredSnapId=saved.preferredSnapId;owner.spatialUserAdjusted=saved.spatialUserAdjusted;owner.runtimeContainment=saved.runtimeContainment;
    owner.updateMatrixWorld(true);updateAttachedMoboChildren(owner);owner.wireframeHelper?.update?.();updateSliderUINodeValues(owner);
}

function reflowCombinations(lists,index=0,prefix=[],out=[]){
    if(index>=lists.length){out.push([...prefix]);return out;}
    for(const item of lists[index]){prefix.push(item);reflowCombinations(lists,index+1,prefix,out);prefix.pop();}
    return out;
}

function tryFanAwareReflow(fanMesh,target){
    const parentChassis=activeCaseBox();
    const owners=fanReflowTargetOwners(target);
    if(!parentChassis||!owners.length)return false;
    const lists=owners.map(fanReflowCandidateList);
    if(lists.some(list=>!list.length))return false;
    const saved=new Map(owners.map(owner=>[owner,savedReflowState(owner)]));
    const restoreAll=()=>owners.forEach(owner=>restoreReflowState(owner,saved.get(owner)));
    const combos=reflowCombinations(lists).slice(0,36);

    for(const combo of combos){
      restoreAll();
      combo.forEach((candidate,index)=>applyFanReflowCandidate(owners[index],candidate,parentChassis));
      if(!owners.every(owner=>componentSafeAfterFanReflow(owner,parentChassis)))continue;
      const blockers=caseFanTargetBlockers(target,fanMesh,activeBoxes);
      if(blockers.length)continue;
      owners.forEach((owner,index)=>{
        const candidate=combo[index];
        owner.activeSnapId=candidate.id;owner.activeMount=candidate.mount||null;owner.activeSnapSource='fan_aware_reflow';
        owner.preferredSnapId=candidate.id;owner.spatialUserAdjusted=false;owner.runtimeContainment={status:'placed',reason:'fan_aware_reflow'};
        owner.wireframeHelper?.update?.();updateSliderUINodeValues(owner);updateComponentPlacementUi(owner);
      });
      return true;
    }
    restoreAll();
    return false;
}

function placedCaseFanTarget(fan){
    if(!fan?.caseFanMountId||!fan?.attachedToCaseUid)return null;
    return {
      id:String(fan.caseFanMountId),
      caseUid:fan.attachedToCaseUid,
      zone:fan.caseFanMountZone||'',face:fan.caseFanMountFace||'',
      frame:Number(fan.caseFanMountFrame||fan.nativeSpecs?.l||fan.nativeSpecs?.w)||120,
      slot:Number(fan.caseFanMountSlot)||0,count:Number(fan.caseFanMountCount)||1,
      worldPosition:fan.getWorldPosition(new THREE.Vector3()),
      worldQuaternion:fan.getWorldQuaternion(new THREE.Quaternion())
    };
}

function enforcePlacedCaseFanReservations(parentChassis,{targetUid=null}={}){
    if(!parentChassis)return true;
    let allClear=true;
    const fans=activeBoxes.filter(item=>item?.trackType==='fan'&&item.attachedToCaseUid===parentChassis.uid&&item.caseFanMountId);
    for(const fan of fans){
      const target=placedCaseFanTarget(fan);if(!target)continue;
      target.blockers=caseFanTargetBlockers(target,fan,activeBoxes);
      if(!target.blockers.length)continue;
      if(tryFanAwareReflow(fan,target))continue;

      // An already-placed source-confirmed fan mount owns its physical volume. If the newly
      // added/re-snapped primary component cannot move to another hard-valid solver candidate,
      // park that component rather than silently letting it consume the fan slot. This makes
      // fan-first and hardware-first workflows converge on the same result.
      const direct=target.blockers.filter(item=>['motherboard','gpu','psu'].includes(item?.trackType));
      const preferred=targetUid?direct.filter(item=>item.uid===targetUid):[];
      const parkList=preferred.length?preferred:direct.filter(item=>!item.spatialUserAdjusted&&!item.isLockedToCase);
      if(!parkList.length){allClear=false;continue;}
      for(const comp of [...new Set(parkList)]){
        parkUnplacedComponent(comp,parentChassis,activeBoxes.filter(b=>b.isComponent).indexOf(comp),{
          valid:false,reason:'reserved_case_fan_mount',reasons:[`case_fan_mount:${target.id}`]
        });
        updateAttachedMoboChildren(comp);
      }
      target.blockers=caseFanTargetBlockers(target,fan,activeBoxes);
      if(target.blockers.length)allClear=false;
    }
    return allClear;
}

function fanPlacementTargets(frame,thickness,fanMesh=null) {
    const parentChassis=activeCaseBox();
    if(!parentChassis)return[];
    const probe=fanMesh || fanPlacementPreview(frame,thickness);
    const caseTargets=buildCaseFanSnapTargets(parentChassis,probe,activeBoxes,{allowSizeMismatch:false})
      .map(target=>({
        id:target.id,kind:'case',target,
        label:`Case · ${String(target.zone||'mount').replace(/_/g,' ').toUpperCase()} · ${Math.round(target.frame||frame)}mm · ${Number(target.slot||0)+1}/${Math.max(1,Number(target.count)||1)}${target.maxThickness?` · ≤${Math.round(target.maxThickness)}mm`:''}`
      }));
    const radiatorTargets=solverFanSlotTargets(probe,{allowSizeMismatch:false})
      .map(entry=>({
        id:`radfan:${entry.radiator.uid}:${entry.slot}`,kind:'radiator',entry,
        label:`Radiator · ${entry.radiator.displayLabel||'AIO'} · fan ${Number(entry.slot)+1}`
      }));

    // If a declared case mount is blocked only by one solver-owned component, expose a
    // bounded "reflow" option. The placement transaction may move that component to one of
    // its already hard-valid alternative candidates, but never moves user-adjusted/locked
    // hardware and never shuffles multiple subsystems at once.
    const blocked=buildCaseFanSnapTargets(parentChassis,probe,activeBoxes,{allowSizeMismatch:false,includeBlocked:true})
      .filter(target=>(target.blockers||[]).length && !caseTargets.some(item=>item.id===target.id));
    const reflowTargets=[];
    for(const target of blocked){
      const owners=fanReflowTargetOwners(target);if(!owners.length)continue;
      reflowTargets.push({
        id:`reflow:${target.id}`,kind:'case_reflow',target,
        label:`Case · ${String(target.zone||'mount').replace(/_/g,' ').toUpperCase()} · ${Number(target.slot||0)+1}/${Math.max(1,Number(target.count)||1)} · reflow ${owners.map(owner=>String(owner.trackType||'hardware').toUpperCase()).join(' + ')}`
      });
    }
    return [...caseTargets,...radiatorTargets,...reflowTargets];
}

function setFanPlacementStatus(message='',tone='') {
    const el=document.getElementById('custom-fan-status');if(!el)return;
    el.textContent=message||'';
    el.style.color=tone==='warn'?'#f59e0b':tone==='ok'?'#10b981':'var(--sub-text)';
}

function refreshFanPlacementUi() {
    const select=document.getElementById('custom-fan-placement');
    if(!select)return;
    const frame=Number(document.getElementById('custom-fan-size')?.value)||120;
    const thickness=Number(document.getElementById('custom-fan-thick')?.value)||25;
    const prior=select.value||'auto';
    const targets=fanPlacementTargets(frame,thickness);
    const options=[
      {id:'auto',label:targets.length?`Auto · next valid mount (${targets.length})`:'Auto · no valid mount'},
      ...targets.map(item=>({id:item.id,label:item.label})),
      {id:'loose',label:'Loose · manual placement'}
    ];
    select.innerHTML=options.map(item=>`<option value="${htmlSafe(item.id)}">${htmlSafe(item.label)}</option>`).join('');
    select.value=options.some(item=>item.id===prior)?prior:'auto';
    select.dataset.availableTargets=String(targets.length);
}
window.refreshFanPlacementOptions=refreshFanPlacementUi;

function clearPendingFanReplacement() {
    if(pendingRadiatorFanReplacement?.autoUid){
      const oldBtn=document.getElementById(`fan_replace_${pendingRadiatorFanReplacement.autoUid}`);
      if(oldBtn){oldBtn.textContent='Replace';oldBtn.style.color='var(--sub-text)';oldBtn.title='Replace this auto fan with the next manually added fan';}
    }
    pendingRadiatorFanReplacement=null;
}

window.armAutoFanReplacement = function(uid) {
    const fan=activeBoxes.find(item=>item?.uid===uid && item?.trackType==='fan' && item?.autoFabricatedFan);
    if(!fan || !fan.attachedToRadiatorUid || !Number.isInteger(Number(fan.assignedSlotIndex)))return false;
    if(pendingRadiatorFanReplacement?.autoUid===uid){clearPendingFanReplacement();return false;}
    clearPendingFanReplacement();
    pendingRadiatorFanReplacement={
      autoUid:fan.uid,
      radiatorUid:fan.attachedToRadiatorUid,
      slotIndex:Number(fan.assignedSlotIndex)
    };
    const btn=document.getElementById(`fan_replace_${uid}`);
    if(btn){btn.textContent='Next Fan →';btn.style.color='#10b981';btn.title='The next fan you add will replace this slot';}
    setFanPlacementStatus(`Next added fan will replace radiator slot ${Number(fan.assignedSlotIndex)+1}.`,'ok');
    return true;
};

function consumePendingFanReplacement(fanMesh) {
    const pending=pendingRadiatorFanReplacement;
    if(!pending || !fanMesh || fanMesh.autoFabricatedFan)return false;
    const radiator=activeBoxes.find(item=>item?.uid===pending.radiatorUid && item?.trackType==='radiator');
    const parentChassis=activeCaseBox();
    if(!radiator || !parentChassis){clearPendingFanReplacement();return false;}
    const plan=activeRadiatorFanPlan(radiator);
    const planItem=plan.find((item,index)=>Number(item?.index ?? index)===Number(pending.slotIndex));
    if(!planItem){clearPendingFanReplacement();return false;}
    const occupiedManual=radiatorManualFanSlots(radiator.uid,fanMesh.uid);
    if(occupiedManual.has(Number(pending.slotIndex))){clearPendingFanReplacement();return false;}

    fanMesh.attachedToRadiatorUid=radiator.uid;
    fanMesh.assignedSlotIndex=Number(pending.slotIndex);
    fanMesh.explicitRadiatorSlotOverride=true;
    removeAutoFabricatedRadiatorFans(radiator.uid,Number(pending.slotIndex));
    const placed=placeFanFromSolverPlan(fanMesh,radiator,{...planItem,index:Number(pending.slotIndex)},parentChassis);
    clearPendingFanReplacement();
    if(placed){manifestUi?.updateFanMountSummary?.(fanMesh);refreshFanPlacementUi();}
    return placed;
}

function checkAndSnapFanToRadiator(fanMesh, retainSlot = false, options = {}) {
    if (fanMesh?.trackType !== 'fan') return false;
    const force=!!options?.force;
    const allowSizeMismatch=!!options?.allowSizeMismatch || !!fanMesh.explicitRadiatorSlotOverride;
    const priorRadiatorUid=fanMesh.attachedToRadiatorUid || null;
    const priorSlot=(fanMesh.assignedSlotIndex !== null && fanMesh.assignedSlotIndex !== undefined && Number.isInteger(Number(fanMesh.assignedSlotIndex)))
      ? Number(fanMesh.assignedSlotIndex) : null;
    const parentChassis=activeCaseBox();

    // Fan snapping now has two evidence sources: exact radiator-stack slots from the hard
    // solve, and case-fan regions compiled from source notes. Source-note slots never accept
    // a size/thickness mismatch; explicit Snap may still override an AIO placeholder size as
    // before because that is a user replacement operation rather than case-capability truth.
    const radiatorTargets=solverFanSlotTargets(fanMesh,{allowSizeMismatch});
    const caseTargets=(!retainSlot && parentChassis)
      ? buildCaseFanSnapTargets(parentChassis,fanMesh,activeBoxes,{allowSizeMismatch:false})
      : [];

    let selectedRadiator=null;
    let selectedCase=null;
    if(retainSlot && priorRadiatorUid && priorSlot!==null){
      selectedRadiator=radiatorTargets.find(entry=>entry.radiator.uid===priorRadiatorUid && entry.slot===priorSlot) || null;
    }else{
      const currentSnap=String(fanMesh.activeFanSnapId||'');
      selectedRadiator=radiatorTargets.find(entry=>`radfan:${entry.radiator.uid}:${entry.slot}`===currentSnap) || null;
      selectedCase=caseTargets.find(entry=>entry.id===currentSnap) || null;
      if(!selectedRadiator&&!selectedCase){
        const rad=radiatorTargets[0]||null;
        const cas=caseTargets[0]||null;
        if(rad&&cas){
          if(rad.distance<=cas.distance)selectedRadiator=rad;
          else selectedCase=cas;
        }else if(rad)selectedRadiator=rad;
        else if(cas)selectedCase=cas;
      }
    }

    const frame=fanMeshFrame(fanMesh) || selectedRadiator?.frame || selectedCase?.frame || 120;
    const proximity=Math.max(55,Math.min(120,frame*0.72));
    const selectedDistance=selectedRadiator?.distance ?? selectedCase?.distance ?? Infinity;
    if((selectedRadiator||selectedCase) && (force || retainSlot || selectedDistance<=proximity)){
      if(selectedRadiator){
        fanMesh.attachedToCaseUid=null;
        fanMesh.caseFanMountId=null;
        fanMesh.caseFanMountSlot=null;fanMesh.caseFanMountCount=null;fanMesh.caseFanMountFrame=null;
        fanMesh.caseFanMountZone=null;
        fanMesh.caseFanMountFace=null;
        fanMesh.attachedToRadiatorUid=selectedRadiator.radiator.uid;
        fanMesh.assignedSlotIndex=selectedRadiator.slot;
        fanMesh.activeFanSnapId=`radfan:${selectedRadiator.radiator.uid}:${selectedRadiator.slot}`;
        // Normal Snap never steals an occupied auto-fabricated radiator slot. The only path
        // that removes one is consumePendingFanReplacement(), armed by the explicit Replace UI.
        if(parentChassis && placeFanFromSolverPlan(fanMesh,selectedRadiator.radiator,selectedRadiator.item,parentChassis)){
          manifestUi?.updateFanMountSummary?.(fanMesh);
          refreshFanPlacementUi();
          return true;
        }
      }else if(selectedCase && parentChassis){
        if(placeFanOnCaseSpatialTarget(fanMesh,selectedCase,parentChassis,scene,activeBoxes)){
          updateComponentLockUi(fanMesh);
          manifestUi?.updateFanMountSummary?.(fanMesh);
          if(priorRadiatorUid)ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,parentChassis);
          refreshFanPlacementUi();
          return true;
        }
      }
    }

    if(!retainSlot){
      fanMesh.attachedToRadiatorUid=null;
      fanMesh.assignedSlotIndex=null;
    }

    // Workbench/fallback snapping for radiators that do not have a solver fan plan.
    const radiators=activeBoxes.filter(b=>b.trackType==='radiator');
    if(!radiators.length){
      if(priorRadiatorUid&&parentChassis)ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,parentChassis);
      manifestUi?.updateFanMountSummary?.(fanMesh);refreshFanPlacementUi();
      return false;
    }
    let closestRadiator=null,minDistance=Infinity;
    const fanWorldPos=new THREE.Vector3();fanMesh.getWorldPosition(fanWorldPos);
    for(const rad of radiators){
      const radWorldPos=new THREE.Vector3();rad.getWorldPosition(radWorldPos);
      const d=fanWorldPos.distanceTo(radWorldPos);
      if(d<minDistance){minDistance=d;closestRadiator=rad;}
    }
    const thresholdDistance=fanMesh.attachedToRadiatorUid?350:95;
    if(!closestRadiator || (!force && minDistance>thresholdDistance)){
      fanMesh.attachedToRadiatorUid=null;fanMesh.assignedSlotIndex=null;
      if(priorRadiatorUid&&parentChassis)ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,parentChassis);
      manifestUi?.updateFanMountSummary?.(fanMesh);refreshFanPlacementUi();
      return false;
    }

    fanMesh.attachedToCaseUid=null;
    fanMesh.caseFanMountId=null;
    fanMesh.caseFanMountSlot=null;fanMesh.caseFanMountCount=null;fanMesh.caseFanMountFrame=null;
    fanMesh.caseFanMountZone=null;
    fanMesh.caseFanMountFace=null;
    fanMesh.attachedToRadiatorUid=closestRadiator.uid;
    const rawLength=closestRadiator.nativeSpecs?closestRadiator.nativeSpecs.l:277;
    const rawHeight=closestRadiator.nativeSpecs?closestRadiator.nativeSpecs.h:27;
    const maxSlots=rawLength<200?1:(rawLength>350?3:2);
    const fanSizeUnit=closestRadiator.nativeSpecs&&closestRadiator.nativeSpecs.w>130?140:120;
    const startingZOffset=-((maxSlots-1)*fanSizeUnit)/2;
    let bestSlotIndex=null;
    const occupiedSlots=radiatorOccupiedFanSlots(closestRadiator.uid,fanMesh.uid);

    if(retainSlot && priorRadiatorUid===closestRadiator.uid && priorSlot!==null && priorSlot<maxSlots && !occupiedSlots.has(priorSlot)){
      bestSlotIndex=priorSlot;
    }else{
      const localFanPos=fanMesh.position.clone().sub(closestRadiator.position).applyQuaternion(closestRadiator.quaternion.clone().invert());
      let closestSlotDistance=Infinity;
      for(let i=0;i<maxSlots;i++){
        if(occupiedSlots.has(i))continue;
        const distanceToSlot=Math.abs(localFanPos.z-(startingZOffset+(i*fanSizeUnit)));
        if(distanceToSlot<closestSlotDistance){closestSlotDistance=distanceToSlot;bestSlotIndex=i;}
      }
    }
    if(bestSlotIndex===null){
      fanMesh.attachedToRadiatorUid=null;fanMesh.assignedSlotIndex=null;
      if(priorRadiatorUid&&parentChassis)ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,parentChassis);
      manifestUi?.updateFanMountSummary?.(fanMesh);refreshFanPlacementUi();
      return false;
    }

    fanMesh.assignedSlotIndex=bestSlotIndex;
    fanMesh.activeFanSnapId=`radfan:${closestRadiator.uid}:${bestSlotIndex}`;
    const absoluteLocalOffset=new THREE.Vector3(0,(rawHeight/2)+(fanMesh.totalModelHeight/2),startingZOffset+(bestSlotIndex*fanSizeUnit)).applyMatrix4(closestRadiator.matrixWorld);
    if(fanMesh.isLockedToCase && fanMesh.parent){
      fanMesh.parent.worldToLocal(absoluteLocalOffset);
      fanMesh.position.copy(absoluteLocalOffset);
      const radWorldQuat=new THREE.Quaternion();closestRadiator.getWorldQuaternion(radWorldQuat);
      fanMesh.quaternion.copy(radWorldQuat);
      const parentWorldQuat=new THREE.Quaternion();fanMesh.parent.getWorldQuaternion(parentWorldQuat);
      fanMesh.quaternion.premultiply(parentWorldQuat.invert());
    }else{
      fanMesh.position.copy(absoluteLocalOffset);
      fanMesh.quaternion.copy(closestRadiator.quaternion);
    }
    fanMesh.relationalOrientationSource='radiator_manual_fallback';
    fanMesh.relationalOrientationTrainingTier='none';
    fanMesh.runtimeContainment={status:'placed',reason:'radiator_manual_fallback'};
    fanMesh.updateMatrixWorld(true);
    if(fanMesh.wireframeHelper)fanMesh.wireframeHelper.update();
    manifestUi?.updateFanMountSummary?.(fanMesh);
    refreshFanPlacementUi();
    return true;
}

function updateSliderUINodeValues(box) {
  getInteractionController()?.updateSliderUi?.(box);
}


function createComponentInstance({ targetL, targetW, targetH, labelName, isCompFlag, trackingSizeStr, trackingColor, trackType='generic', componentMetaOverride=null, suppressRebuild=false }) {
  const wrapper=createProceduralComponent({trackType,l:targetL,w:targetW,h:targetH,label:labelName,color:trackingColor,isComponent:isCompFlag,componentMeta:componentMetaOverride});
  if (componentMetaOverride && typeof componentMetaOverride === 'object') wrapper.componentMeta = { ...wrapper.componentMeta, ...componentMetaOverride };
  wrapper.catalogId=String(wrapper.componentMeta?.catalogId||'').trim();
  wrapper.displayLabel = labelName;
  scene.add(wrapper); activeBoxes.push(wrapper);
  if (activeEngineMode==='simulator' && activeCaseBox() && !suppressRebuild) rebuildSimulatorPlan({realign:true, reason:'component_spawn'});
  else if (activeEngineMode!=='simulator') {
    if(trackType==='aircooler'&&checkAndAttachToMotherboardSocket(wrapper)){}
    else if(trackType==='ram'&&checkAndAttachRamToMotherboard(wrapper)){}
    else wrapper.position.copy(getNextComponentSpawnPosition(targetH));
  }
  const cageHelper=new THREE.BoxHelper(wrapper,isCompFlag?(wrapper.categoryColor||trackingColor||0x38bdf8):0xffffff); scene.add(cageHelper); wrapper.wireframeHelper=cageHelper;
  const manifestColor=wrapper.categoryColor||trackingColor;
  manifestUi?.attachHtmlBadgeNode?.(wrapper,labelName); manifestUi?.appendDomManifestRow?.(wrapper,labelName,trackingSizeStr,manifestColor,isCompFlag); manifestUi?.checkEmptyState?.();
  if(activeEngineMode==='simulator')updateSliderUINodeValues(wrapper);
  else aioAssemblyManager?.ensure?.();
  return wrapper;
}

window.loadProceduralHardware = function(targetL,targetW,targetH,labelName,isCompFlag,trackingSizeStr,trackingColor,trackType='generic',componentMetaOverride=null) {
  const inferredMeta={...inferComponentMeta(trackType,labelName,{l:targetL,w:targetW,h:targetH}),...(componentMetaOverride||{})};
  if (isCompFlag && ((activeEngineMode==='simulator' && activeCaseBox()) || trackType==='ram' || trackType==='aircooler')) {
    const eligibility=hardwareEligibility(trackType,{l:targetL,w:targetW,h:targetH},inferredMeta);
    if(!eligibility.allowed){
      window.lastHardwareEligibilityFailure={trackType,label:labelName,dims:{l:targetL,w:targetW,h:targetH},meta:inferredMeta,...eligibility};
      getNearFitController()?.reportFailure?.({
        trackType,label:labelName,dims:{l:targetL,w:targetW,h:targetH},meta:inferredMeta,reason:eligibility.reason
      });
      console.warn(`[SFF 3D] ${labelName} blocked: ${eligibility.reason}`);
      refreshHardwareEligibilityUi();
      return false;
    }
  }
  if (isCompFlag && activeEngineMode==='simulator') {
    // Inventory changes require a fresh solve, but they do not invalidate manual transforms
    // on OTHER hardware in the same case/mode coordinate frame. The replaced slot is removed
    // and the new object is solver-owned; existing user-adjusted objects stay where placed.
    replaceSimulatorSlotIfNeeded(trackType);
  }
  const wrapper = createComponentInstance({targetL,targetW,targetH,labelName,isCompFlag,trackingSizeStr,trackingColor,trackType,componentMetaOverride:inferredMeta,suppressRebuild:true});
  if (activeEngineMode==='simulator' && activeCaseBox()) {
    if(trackType==='ram'){
      const attached=checkAndAttachRamToMotherboard(wrapper);
      if(!attached)wrapper.position.copy(getNextComponentSpawnPosition(targetH));
      wrapper.runtimeContainment={status:attached?'placed':'unplaced',reason:attached?(wrapper.componentMeta?.placementSource||'motherboard_ram_slots_reference'):'motherboard_required'};
      updateComponentPlacementUi(wrapper);
      updateSliderUINodeValues(wrapper);
      workbenchRuntime?.markSpatialDirty?.();
    } else {
      rebuildSimulatorPlan({realign:true, realignPolicy:'preserve_manual', realignUid:wrapper?.uid||null, reason:'hardware_change'});
    }
  }
  return !!wrapper;
};
window.handleCustomRamSubmit = function() {
  const input=document.getElementById('custom-ram-height');
  const status=document.getElementById('custom-ram-status');
  const height=Number(input?.value)||0;
  if(height<10||height>100){
    if(status){status.textContent='Enter a RAM height from 10 to 100 mm.';status.style.color='#f59e0b';}
    return false;
  }
  const ok=window.loadProceduralHardware(133,7,height,`Custom RAM (${Math.round(height*10)/10}mm)`,true,`${Math.round(height*10)/10} mm RAM height`,0xc084fc,'ram',{
    ramHeight:height,ramHeightKnown:true,memoryType:'CUSTOM',rgb:false,ramKitModules:2,catalogId:'',
    dimensionKnown:{l:false,w:false,h:true},dimensionSource:'custom_ram_height_visual_udimm_envelope'
  });
  if(status){
    status.textContent=ok?'':String(window.lastHardwareEligibilityFailure?.reason||'RAM could not be added.');
    status.style.color=ok?'var(--sub-text)':'#f59e0b';
  }
  return ok;
};

window.handleCustomFanSubmit = function() {
  const frameSize=parseFloat(document.getElementById('custom-fan-size')?.value)||120;
  const thickness=parseFloat(document.getElementById('custom-fan-thick')?.value)||25;
  const placement=String(document.getElementById('custom-fan-placement')?.value||'auto');
  return window.executeProceduralFanFallback(frameSize,thickness,`Fan (${frameSize}x${thickness}mm)`,`${frameSize} x ${thickness} x ${frameSize} mm`,0x10b981,placement);
};

window.executeProceduralFanFallback = function(frameSize, thickness, labelName, trackingSizeStr, assignedColor, placementOverride='auto') {
    const placement=String(placementOverride||'auto');
    let planned=null;
    if(activeEngineMode==='simulator' && activeCaseBox() && !pendingRadiatorFanReplacement && placement!=='loose'){
      const targets=fanPlacementTargets(frameSize,thickness);
      planned=placement==='auto'?targets[0]:targets.find(item=>item.id===placement);
      if(!planned){
        setFanPlacementStatus('No free compatible mount. Choose Loose for manual placement.','warn');
        refreshFanPlacementUi();
        return false;
      }
    }

    const geometry = new THREE.BoxGeometry(frameSize, thickness, frameSize);
    const edgeMat = new THREE.MeshStandardMaterial({ color: 0x232529, roughness: 0.6 });
    const fanMesh = new THREE.Mesh(geometry, edgeMat);
    fanMesh.castShadow = true; fanMesh.receiveShadow = true;
    fanMesh.isComponent = true; fanMesh.trackType = 'fan';
    fanMesh.totalModelHeight = thickness; fanMesh.nativeSpecs = { l: frameSize, w: frameSize, h: thickness };
    fanMesh.uid = 'sff' + Date.now() + '_' + Math.random().toString(36).slice(2,5);
    fanMesh.position.copy(getNextComponentSpawnPosition(thickness));

    scene.add(fanMesh); activeBoxes.push(fanMesh);
    const cageHelper = new THREE.BoxHelper(fanMesh, assignedColor); scene.add(cageHelper); fanMesh.wireframeHelper = cageHelper;
    manifestUi?.attachHtmlBadgeNode?.(fanMesh, labelName); manifestUi?.appendDomManifestRow?.(fanMesh, labelName, trackingSizeStr, assignedColor, true);

    let placed=false;
    if(activeEngineMode==='simulator' && activeCaseBox()) {
        if(consumePendingFanReplacement(fanMesh)){
          placed=true;
        }else if(placement==='loose'){
          fanMesh.runtimeContainment={status:'unplaced',reason:'user_requested_loose_fan'};
          fanMesh.activeFanSnapId=null;
        }else if(planned?.kind==='case_reflow'){
          if(tryFanAwareReflow(fanMesh,planned.target)){
            placed=placeFanOnCaseSpatialTarget(fanMesh,planned.target,activeCaseBox(),scene,activeBoxes);
            if(placed)updateComponentLockUi(fanMesh);
          }
        }else if(planned?.kind==='case'){
          placed=placeFanOnCaseSpatialTarget(fanMesh,planned.target,activeCaseBox(),scene,activeBoxes);
          if(placed)updateComponentLockUi(fanMesh);
        }else if(planned?.kind==='radiator'){
          const {entry}=planned;
          fanMesh.attachedToRadiatorUid=entry.radiator.uid;
          fanMesh.assignedSlotIndex=entry.slot;
          fanMesh.activeFanSnapId=`radfan:${entry.radiator.uid}:${entry.slot}`;
          placed=placeFanFromSolverPlan(fanMesh,entry.radiator,entry.item,activeCaseBox());
        }
        if(placement!=='loose'&&!placed){
          // The target may have become blocked between option rendering and Add. Do not leave
          // another loose fan at the generic spawn point when the user asked for a mount.
          window.removeModelInstance?.(fanMesh.uid,{suppressRebuild:true,internalTransaction:true});
          setFanPlacementStatus('Selected mount is no longer available.','warn');
          refreshFanPlacementUi();
          return false;
        }
        ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,activeCaseBox());
        updateSliderUINodeValues(fanMesh);
    }
    manifestUi?.updateFanMountSummary?.(fanMesh);
    manifestUi?.checkEmptyState?.();
    refreshFanPlacementUi();
    const mountText=fanMesh.caseFanMountZone?`Case ${String(fanMesh.caseFanMountZone).replace(/_/g,' ')}`:(fanMesh.attachedToRadiatorUid?'Radiator stack':(placement==='loose'?'Loose':'Mounted'));
    setFanPlacementStatus(mountText,'ok');
    return true;
};

window.handleComponentDropdownSelection = function(dropdownElement) {
  const val=dropdownElement.value;if(!val)return;
  const selectedOption=dropdownElement.options[dropdownElement.selectedIndex];
  if(selectedOption?.disabled){dropdownElement.value='';return;}
  if(val==='itxmobo')window.loadProceduralHardware(170,170,44,'Mini-ITX Board',true,'170 x 170 x 44 mm',0x38bdf8,'motherboard');
  else if(val==='dtxmobo')window.loadProceduralHardware(203,170,44,'DTX Board',true,'203 x 170 x 44 mm',0x38bdf8,'motherboard');
  else if(val==='matxmobo')window.loadProceduralHardware(244,244,44,'Micro-ATX Board',true,'244 x 244 x 44 mm',0x38bdf8,'motherboard');
  else if(val==='atxmobo')window.loadProceduralHardware(305,244,44,'ATX Board',true,'305 x 244 x 44 mm',0x38bdf8,'motherboard');
  else if(val==='sfxpsu')window.loadProceduralHardware(125,63.5,100,'SFX Power Supply',true,'125 x 63.5 x 100 mm',0x2b2c2f,'psu');
  else if(val==='sfxlpsu')window.loadProceduralHardware(125,63.5,125,'SFX-L Power Supply',true,'125 x 63.5 x 125 mm',0x1a1b1d,'psu');
  else if(val==='atxpsu')window.loadProceduralHardware(150,86,140,'ATX Power Supply',true,'150 x 86 x 140 mm',0x151619,'psu');
  else if(val==='flexpsu')window.loadProceduralHardware(150,40.5,81.5,'Flex-ATX Power Supply',true,'150 x 40.5 x 81.5 mm',0x151619,'psu');
  dropdownElement.value='';
};
function buildCaseSpatialControls(box) {
  const overlay=box?.structuredOverlay||null;
  const modes=getActualModes(overlay);
  const activeMode=modes.find(m=>String(m.mode_id||m.id||'')===String(box.spatialState?.modeId||''))||modes[0]||null;
  const localSteps=Array.isArray(activeMode?.fixedSteps)?activeMode.fixedSteps:[];
  const globalSteps=Array.isArray(overlay?.fixedSteps)?overlay.fixedSteps:[];
  const steps=localSteps.length?localSteps:globalSteps;
  // Only real structured modes are displayed. BASE / DEFAULT used to be a fabricated UI
  // state and could duplicate/invent a configuration that does not exist in cases_structured.
  const modeOptions=modes.map(m=>{const id=String(m.mode_id||m.id||'');return `<option value="${htmlSafe(id)}" ${box.spatialState?.modeId===id?'selected':''}>${htmlSafe(m.mode_name||m.name||id)}</option>`;}).join('');
  const normStep=value=>String(value||'').trim().toLowerCase().replace(/[^a-z0-9]+/g,'');
  const activeModeKeys=new Set([activeMode?.mode_id,activeMode?.id,activeMode?.mode_name,activeMode?.name,activeMode?.slotName].map(normStep).filter(Boolean));
  const autoMatchedStep=!box.spatialState?.fixedStep ? steps.find(step=>[step?.slotName,step?.id,step?.mode_id,step?.name].map(normStep).some(key=>key&&activeModeKeys.has(key))) : null;
  const autoLabel=autoMatchedStep?`AUTO / MODE (${String(autoMatchedStep.slotName||autoMatchedStep.id||'STEP')})`:'AUTO / NONE';
  const stepOptions=[`<option value="">${htmlSafe(autoLabel)}</option>`].concat(steps.map(s=>{const id=String(s.slotName||s.id||'');return `<option value="${htmlSafe(id)}" ${box.spatialState?.fixedStep===id?'selected':''}>${htmlSafe(id)}</option>`;})).join('');
  return `<div id="spatial_controls_${box.uid}" class="sff-spatial-case-controls" style="display:flex;flex-direction:column;gap:4px;border-top:1px dashed var(--sidebar-border);padding-top:5px;">
    <div style="font-size:9px;color:var(--sub-text);display:flex;justify-content:space-between;gap:8px;"><span>SOLVED TOPOLOGY</span><strong id="topology_${box.uid}" style="color:#38bdf8;text-align:right;">SOLVING…</strong></div>
    ${modes.length?`<label style="font-size:9px;color:var(--sub-text);">CASE MODE<select class="sff-dropdown font-mono" style="margin-top:2px;height:25px;" onchange="setCaseSpatialMode('${box.uid}',this.value)">${modeOptions}</select></label>`:''}
    ${steps.length?`<label style="font-size:9px;color:var(--sub-text);">SPINE / STEP<select class="sff-dropdown font-mono" style="margin-top:2px;height:25px;" onchange="setCaseSpineStep('${box.uid}',this.value)">${stepOptions}</select></label>`:''}
  </div>`;
}
function updateCaseTopologyStatus(box){
  const el=document.getElementById(`topology_${box?.uid}`); if(!el)return;
  const plan=box?.spatialPlan;
  if(!plan){el.textContent='UNRESOLVED';el.style.color='#f59e0b';return;}
  const confidence=Math.round((Number(plan.topologyConfidence)||0)*100);
  const family=String(plan.layoutFamily||'unknown').replace(/_/g,' ').toUpperCase();
  const transform=String(plan.layoutVariant||'standard').replace(/_/g,' ').toUpperCase();
  const label=transform==='STANDARD'?family:`${family} · ${transform}`;
  const source=plan.placementSelectionSource==='relational_gnn_ranker'?'GNN':'FALLBACK';
  el.textContent=`${label} · ${confidence}% · ${source}`;
  el.style.color=plan.placementSelectionSource==='relational_gnn_ranker'?'#a78bfa':(confidence>=70?'#10b981':confidence>=45?'#f59e0b':'#f43f5e');
  const placementConfidence=Math.round((Number(plan.placementConfidence)||0)*100);
  el.title=[...(plan.topologyReasons||[]),`topology:${confidence}%`,`placement:${source.toLowerCase()}`,`placement-gap:${placementConfidence}%`].join(' · ');
}
window.setCaseSpatialMode=function(uid,modeId){const box=activeBoxes.find(b=>b.uid===uid&&b.isCase);if(!box)return;box.spatialState.modeId=modeId||null;box.spatialState.modeSource='user';box.spatialState.fixedStep=null;const controls=document.getElementById(`spatial_controls_${uid}`);if(controls)controls.outerHTML=buildCaseSpatialControls(box);prepareComponentsForFreshSolve('case_mode_change');rebuildSimulatorPlan({realign:true,reason:'case_mode_change'});};
window.setCaseSpineStep=function(uid,stepId){const box=activeBoxes.find(b=>b.uid===uid&&b.isCase);if(!box)return;box.spatialState.fixedStep=stepId||null;prepareComponentsForFreshSolve('spine_step_change');rebuildSimulatorPlan({realign:true,reason:'spine_step_change'});};

window.removeModelInstance = function(uid, options = {}) {
    const suppressRebuild = !!options?.suppressRebuild;
    const internalTransaction = !!options?.internalTransaction;
    const idx = activeBoxes.findIndex(b => b.uid === uid); if (idx === -1) return;
    const target = activeBoxes[idx];
    if(pendingRadiatorFanReplacement && (target?.uid===pendingRadiatorFanReplacement.autoUid || target?.uid===pendingRadiatorFanReplacement.radiatorUid || target?.isCase)) clearPendingFanReplacement();
    const primarySlot = target?.isComponent ? componentSlotForTrackType(target.trackType) : null;
    const restoreRadiatorUid = target?.trackType==='fan' && !target.autoFabricatedFan ? target.attachedToRadiatorUid : null;
    if(target?.isCase)aioAssemblyManager?.clear?.();
    else aioAssemblyManager?.removeForObject?.(target);
    if(target?.trackType==='motherboard'){
        // RAM is a child of the motherboard for transform authority, but it is still its own
        // logical inventory item. Detach it before deleting/replacing the board so the RAM kit
        // survives and can snap onto the next motherboard.
        activeBoxes.filter(item=>item?.trackType==='ram'&&item.attachedToMoboUid===target.uid).forEach(ram=>{
          scene.attach(ram);
          ram.attachedToMoboUid=null;
          ram.activeSnapId=null;
          ram.activeSnapSource='motherboard_required';
          ram.runtimeContainment={status:'unplaced',reason:'motherboard_required'};
          ram.wireframeHelper?.update?.();
          manifestUi?.updateRamMountSummary?.(ram);
        });
    }
    if(target?.trackType==='radiator'){
        // Fans are real transform children of the radiator stack. Remove their scene/UI
        // records transactionally before deleting the parent so activeBoxes cannot retain
        // detached child references.
        [...activeBoxes].filter(item=>item?.trackType==='fan'&&item.attachedToRadiatorUid===target.uid)
          .forEach(item=>window.removeModelInstance?.(item.uid,{suppressRebuild:true,internalTransaction:true}));
    }
    if (target.wireframeHelper) {
        scene.remove(target.wireframeHelper);
        workbenchRuntime?.disposeObject3DResources?.(target.wireframeHelper);
        target.wireframeHelper = null;
    }

    if (target.isCase) {
        activeBoxes.forEach(b => {
            if (b.isComponent && b.isLockedToCase) {
                if(!(b.trackType==='fan'&&b.attachedToRadiatorUid&&!b.fanAssemblyDetached))scene.attach(b);
                b.isLockedToCase = false;
            }
        });
    }

    if (target.parent) target.parent.remove(target);
    workbenchRuntime?.disposeObject3DResources?.(target);
    if (target.badgeEl && target.badgeEl.parentNode) target.badgeEl.parentNode.removeChild(target.badgeEl);
    const domRow = document.getElementById(`item_${uid}`); if (domRow) domRow.remove();
    activeBoxes.splice(idx, 1); manifestUi?.checkEmptyState?.();
    if(activeEngineMode!=='simulator')aioAssemblyManager?.ensure?.();

    if (!suppressRebuild && activeEngineMode === 'simulator' && activeCaseBox()) {
        if (primarySlot) {
            rebuildSimulatorPlan({realign:true, realignPolicy:'preserve_manual', reason:'hardware_remove'});
        } else if (restoreRadiatorUid && !internalTransaction) {
            // Removing a user replacement releases only that radiator slot. Recreate the
            // solver placeholder without paying for a complete spatial solve.
            ensureAutoFabricatedRadiatorFans(SFF_SimulatorEngine.activeBlueprint,activeCaseBox());
        }
        // Removing an auto-fabricated placeholder is intentionally rebuild-free so the user
        // can immediately add a custom fan into that slot without the placeholder reappearing.
    }
    if (!activeCaseBox()) getNearFitController()?.notifyBuildChanged?.();
    if(target?.trackType==='ram'||target?.trackType==='aircooler')refreshHardwareEligibilityUi();
    manifestUi?.updateMobileManifestCountBadge?.();
    refreshFanPlacementUi();
};

function triggerCaseSpawn(name,caseDimensions,catalogRecordOverride=null) {
  const isSimulator = activeEngineMode==='simulator';
  const preservedHardware = isSimulator ? snapshotPrimaryHardware(activeBoxes) : [];
  const preservedRam = isSimulator ? activeBoxes.filter(comp=>comp?.isComponent&&comp.trackType==='ram').map(comp=>({
    trackType:'ram',
    label:String(comp.badgeEl?.textContent || comp.displayLabel || 'RAM'),
    color:Number(comp.categoryColor || 0xc084fc),
    dims:{l:Number(comp.nativeSpecs?.l)||133,w:Number(comp.nativeSpecs?.w)||7,h:Number(comp.nativeSpecs?.h)||0},
    componentMeta:{...(comp.componentMeta||{})}
  })) : [];

  if(isSimulator) {
    // Treat a case swap as an atomic scene transaction. Reusing Three objects across
    // chassis solves proved too stateful (parent transforms, manual orientation, lock
    // state and old snap identity could survive in combinations). Preserve hardware
    // identity/specs only; rebuild fresh scene objects after the new case exists.
    removePrimarySimulatorComponentsForTransaction();
    activeBoxes.filter(b=>b.isComponent).forEach(c=>window.removeModelInstance(c.uid,{suppressRebuild:true,internalTransaction:true}));
    activeBoxes.filter(b=>b.isCase).forEach(c=>window.removeModelInstance(c.uid,{suppressRebuild:true,internalTransaction:true}));
    SFF_SimulatorEngine.activeBlueprint = null;
  }
  const searchKey=name.toLowerCase().trim(); let assignedColor=EXPANDED_COLOR_POOL[activeBoxes.filter(b=>b.isCase).length%EXPANDED_COLOR_POOL.length];
  for(const key in PRESET_COLORS){if(searchKey.includes(key)){assignedColor=PRESET_COLORS[key];break;}}
  const parts=caseDimensions.replace(/ mm/g,'').split(' x ').map(Number);const l=parts[0]||200,w=parts[1]||150,h=parts[2]||200;
  const wrapperGroup=new THREE.Group();const caseMesh=createHardwareBoxMesh(l,w,h,assignedColor,name,false);wrapperGroup.add(caseMesh);
  wrapperGroup.uid='sff'+Date.now()+'_'+Math.random().toString(36).slice(2,6);wrapperGroup.isComponent=false;wrapperGroup.isCase=true;
  const matchedDbRecord=catalogRecordOverride
    || getCaseIndexBySlug().get(caseRouteSlug(name))
    || getCaseIndexByNormalizedName().get(normalizePresetName(name))
    || getCasesDataset().find(c=>c.name.toLowerCase().includes(searchKey))
    || {name,l,w,h,style:'reference',maxCPU:65,maxGPU:300,maxThick:50,maxGPUHeight:140,maxAIO:0,maxPSU:0};
  wrapperGroup.catalogRecord={...matchedDbRecord,l,w,h};wrapperGroup.catalogId=String(matchedDbRecord?.catalogId||matchedDbRecord?.id||'').trim();wrapperGroup.structuredOverlay=getStructuredOverlay(matchedDbRecord,getStructuredIndex());wrapperGroup.spatialState=createCaseSpatialState(wrapperGroup.catalogRecord,wrapperGroup.structuredOverlay);
  wrapperGroup.nativeSpecs={l,w,h,name,style:matchedDbRecord.style||'reference',maxCPU:matchedDbRecord.maxCPU,maxGPU:matchedDbRecord.maxGPU,maxThick:matchedDbRecord.maxThick,maxGPUHeight:matchedDbRecord.maxGPUHeight};
  renderCaseSpatialEvidenceMarkers(wrapperGroup);
  const feetToggle=document.getElementById('custom-case-feet-toggle'),feetInput=document.getElementById('custom-case-feet-height');const feetHeight=(feetToggle&&feetToggle.checked&&feetInput)?parseFloat(feetInput.value)||0:0;wrapperGroup.addedFeetHeight=feetHeight;
  if(feetHeight>0){const footGeom=new THREE.BoxGeometry(14,feetHeight,14),footMat=new THREE.MeshStandardMaterial({color:0x121318,roughness:0.6}),padX=w/2-12,padZ=l/2-12;[[-padX,-padZ],[padX,-padZ],[-padX,padZ],[padX,padZ]].forEach(([x,z])=>{const foot=new THREE.Mesh(footGeom,footMat);foot.position.set(x,-h/2-feetHeight/2,z);foot.isFoot=true;wrapperGroup.add(foot);});}
  if(isSimulator)wrapperGroup.position.set(0,h/2+feetHeight,120);else{const n=activeBoxes.filter(b=>b.isCase).length;wrapperGroup.position.set(n===0?-280:280,h/2+feetHeight,n===0?-50:0);}
  scene.add(wrapperGroup);activeBoxes.push(wrapperGroup);const cageHelper=new THREE.BoxHelper(caseMesh,0xffffff);scene.add(cageHelper);wrapperGroup.wireframeHelper=cageHelper;
  manifestUi?.attachHtmlBadgeNode?.(wrapperGroup,formatCaseBrandTitle(name));manifestUi?.appendDomManifestRow?.(wrapperGroup,formatCaseBrandTitle(name),caseDimensions,assignedColor,false);manifestUi?.checkEmptyState?.();

  if(isSimulator){
    preservedHardware.forEach(snapshot => spawnHardwareSnapshot(snapshot,{suppressRebuild:true}));
    preservedRam.forEach(snapshot => spawnHardwareSnapshot(snapshot,{suppressRebuild:true}));
    rebuildSimulatorPlan({realign:true, reason:'case_transaction'});
    centerCameraOnActiveCase();
  }
  refreshFanPlacementUi();
}

window.handleCustomCaseSubmit = function() {
    const l = parseFloat(document.getElementById('custom-case-l').value) || 350;
    const w = parseFloat(document.getElementById('custom-case-w').value) || 150;
    const h = parseFloat(document.getElementById('custom-case-h').value) || 300;
    triggerCaseSpawn("Custom Enclosure", `${l} x ${w} x ${h} mm`);
};

// =========================================================================
// RELATIONAL SPATIAL SNAPSHOT BRIDGE
// Read-only bridge for future verified spatial evidence. Nothing here is
// automatically uploaded or treated as compatibility ground truth.
// =========================================================================
window.getSpatialSolveDebug = function() {
  const caseBox = activeCaseBox();
  return {
    epoch: simulatorSolveEpoch,
    fingerprint: lastSpatialSolveFingerprint,
    case: caseBox?.catalogRecord?.name || caseBox?.nativeSpecs?.name || null,
    caseCatalogId:caseBox?.catalogId || caseBox?.catalogRecord?.catalogId || caseBox?.catalogRecord?.id || null,
    physicalLayout: {
      catalog: caseBox?.catalogRecord?.physicalLayout || null,
      effectiveId: SFF_SimulatorEngine.activeBlueprint?.effective?.physicalLayoutId || null,
      orientation: SFF_SimulatorEngine.activeBlueprint?.effective?.physicalOrientation || null,
      transform: SFF_SimulatorEngine.activeBlueprint?.effective?.layoutTransform || null
    },
    topology: SFF_SimulatorEngine.activeBlueprint?.topologyId || null,
    confidence: Number(SFF_SimulatorEngine.activeBlueprint?.topologyConfidence || 0),
    placementConfidence: Number(SFF_SimulatorEngine.activeBlueprint?.placementConfidence || 0),
    reason: SFF_SimulatorEngine.activeBlueprint?.solveReason || null,
    placementSelectionSource:SFF_SimulatorEngine.activeBlueprint?.placementSelectionSource || null,
    gnnRanking:SFF_SimulatorEngine.activeBlueprint?.gnnRanking || null,
    hasHardValidSolution:!!SFF_SimulatorEngine.activeBlueprint?.hasHardValidSolution,
    hardDiagnostics:SFF_SimulatorEngine.activeBlueprint?.hardDiagnostics || null,
    componentValidity:SFF_SimulatorEngine.activeBlueprint?.componentValidity || {},
    structuralReservations:SFF_SimulatorEngine.activeBlueprint?.structuralReservations || null,
    configurationBranch:SFF_SimulatorEngine.activeBlueprint?.configurationBranch || null,
    configurationBranches:SFF_SimulatorEngine.activeBlueprint?.configurationBranches || [],
    ranker:spatialRankerStatus(),
    catalog: getCatalogDiagnostics(),
    spatialEvidence: {
      schema:Number(caseBox?.spatialEvidence?.schema || caseBox?.catalogRecord?.spatialHints?.schema || 0),
      frame:caseBox?.spatialEvidenceFrame || 'physical',
      fanMounts:caseBox?.spatialEvidence?.fans?.length || 0,
      radiatorMounts:caseBox?.spatialEvidence?.radiators?.length || 0,
      aioAssemblies:aioAssemblyManager?.size || 0
    },
    capabilityPool: SFF_SimulatorEngine.activeBlueprint?.capabilityProfile ? {
      states: SFF_SimulatorEngine.activeBlueprint.capabilityProfile.states?.length || 0,
      motherboards: SFF_SimulatorEngine.activeBlueprint.capabilityProfile.motherboardTypes || [],
      psus: SFF_SimulatorEngine.activeBlueprint.capabilityProfile.psuTypes || [],
      gpuEnvelopes: SFF_SimulatorEngine.activeBlueprint.capabilityProfile.gpuEnvelopes || [],
      radiatorSizes: SFF_SimulatorEngine.activeBlueprint.capabilityProfile.radiatorSizes || []
    } : null,
    hardware: snapshotPrimaryHardware(activeBoxes),
    placements: activeBoxes.filter(b=>b.isComponent).map(b=>({
      type:b.trackType,
      catalogId:b.catalogId || b.componentMeta?.catalogId || null,
      label:b.displayLabel || '',
      position:b.getWorldPosition(new THREE.Vector3()).toArray(),
      orientationClass:b.relationalOrientationClass ?? null,
      snap:b.activeSnapId || b.activeFanSnapId || null,
      containment:b.runtimeContainment || null,
      advisories:Array.isArray(b.runtimeAdvisories)?b.runtimeAdvisories:[],
      expectedWorldSize:b.lastSolverExpectedWorldSize || null,
      actualWorldSize:calculateCleanBox(b).getSize(new THREE.Vector3()).toArray(),
      sizeDelta:b.lastSolverExpectedWorldSize ? calculateCleanBox(b).getSize(new THREE.Vector3()).toArray().map((v,i)=>Number((v-(b.lastSolverExpectedWorldSize[i]||0)).toFixed(3))) : null
    }))
  };
};

function importSffBuildBridgePayload(payload, resolveById = () => null) {
  if(!payload || Number(payload.schema)!==2 || Number(payload.identitySchema)!==1) return {ok:false,reason:'unsupported_bridge_schema'};
  if(activeEngineMode!=='simulator') window.switchToolWorkspaceMode?.('simulator');

  // Import is an atomic build-identity transaction. Remove current hardware before any case
  // swap so triggerCaseSpawn cannot preserve components from the previous build.
  activeBoxes.filter(item=>item?.isComponent).forEach(item=>window.removeModelInstance?.(item.uid,{suppressRebuild:true,internalTransaction:true}));

  const missing=[];
  const loaded=[];
  const resolveRef=(ref,expectedKinds=[])=>{
    if(!ref?.id)return null;
    const hit=resolveById(ref.id)||null;
    if(!hit)return null;
    const kind=String(hit.kind||'').toLowerCase();
    const expected=expectedKinds.map(v=>String(v).toLowerCase());
    if(expected.length && !expected.includes(kind))return null;
    return hit;
  };

  const caseHit=resolveRef(payload.case,['case']);
  if(caseHit?.record){
    const record=caseHit.record;
    const id=String(record.catalogId||record.id||'').trim();
    const current=activeCaseBox();
    if(!current || String(current.catalogId||'').trim()!==id){
      triggerCaseSpawn(formatCaseBrandTitle(record.name||'SFF Case'),`${record.l} x ${record.w} x ${record.h} mm`,record);
    }
  }else if(payload.case?.custom && !activeCaseBox()){
    const d=payload.case.dims||{};
    triggerCaseSpawn(String(payload.case.label||'Custom Enclosure'),`${Number(d.l)||200} x ${Number(d.w)||150} x ${Number(d.h)||200} mm`);
  }else if(payload.case?.id){
    missing.push({type:'case',id:payload.case.id});
  }

  const caseBox=activeCaseBox();
  if(!caseBox)return {ok:false,reason:'bridge_case_unresolved',missing};

  if(payload.modeId){
    const modes=getActualModes(caseBox.structuredOverlay);
    const requested=String(payload.modeId);
    const matched=modes.find(mode=>String(mode.mode_id||mode.id||'')===requested)
      || modes.find(mode=>String(mode?._sourceModeId||'')===requested && mode?._expandedSpatialVariant==='classic');
    if(matched)caseBox.spatialState.modeId=String(matched.mode_id||matched.id||requested);
    else missing.push({type:'mode',id:requested});
  }
  caseBox.spatialState.fixedStep=payload.fixedStep||null;
  caseBox.spatialState.modeSource='portable_build_bridge';

  const customColors={motherboard:0x38bdf8,gpu:0x38bdf8,psu:0xf87171,aircooler:0xa1a1aa,radiator:0x007dec,ram:0xc084fc};
  const requirements=payload.requirements&&typeof payload.requirements==='object'?payload.requirements:{};
  const finitePositive=value=>{const n=Number(value);return Number.isFinite(n)&&n>0?n:0};
  const normalizeMobo=value=>{
    const raw=String(value||'ITX').trim().toUpperCase().replace(/_/g,'-');
    if(/^(?:M-?ATX|MICRO-?ATX|MICRO ATX|MATX)$/.test(raw))return 'MATX';
    if(/^(?:MINI-?DTX|MINI DTX|DTX)$/.test(raw))return 'DTX';
    if(/^(?:MINI-?ITX|MINI ITX|ITX)$/.test(raw))return 'ITX';
    if(raw==='ATX')return 'ATX';
    return 'ITX';
  };
  const normalizePsu=value=>{
    const raw=String(value||'SFX').trim().toUpperCase().replace(/_/g,'-');
    if(raw==='SFXL'||raw==='SFX-L')return 'SFX-L';
    if(raw==='ATX')return 'ATX';
    if(raw.includes('FLEX'))return 'FLEX';
    return 'SFX';
  };
  const fallbackRefFromRequirements=payloadKey=>{
    if(payloadKey==='motherboard'){
      const rawMobo=requirements.mobo??requirements.m;
      if(rawMobo===undefined||rawMobo===null||String(rawMobo).trim()==='')return null;
      const ff=normalizeMobo(rawMobo);
      const dims={ITX:{l:170,w:170,h:1.6},DTX:{l:203,w:170,h:1.6},MATX:{l:244,w:244,h:1.6},ATX:{l:305,w:244,h:1.6}}[ff];
      return {custom:true,label:`${ff} Motherboard`,dims,meta:{formFactor:ff}};
    }
    if(payloadKey==='gpu'){
      const l=finitePositive(requirements.gpuL??requirements.gl),w=finitePositive(requirements.gpuT??requirements.gt),h=finitePositive(requirements.gpuH??requirements.gh);
      return l&&w&&h?{custom:true,label:'Recovered GPU Requirement',dims:{l,w,h}}:null;
    }
    if(payloadKey==='psu'){
      const rawPsu=requirements.psu??requirements.p;
      if(rawPsu===undefined||rawPsu===null||String(rawPsu).trim()==='')return null;
      const ff=normalizePsu(rawPsu);
      const dims={SFX:{l:125,w:63.5,h:100},'SFX-L':{l:125,w:63.5,h:125},ATX:{l:150,w:86,h:140},FLEX:{l:150,w:40.5,h:81.5}}[ff];
      return dims?{custom:true,label:`${ff} Power Supply`,dims,meta:{formFactor:ff}}:null;
    }
    if(payloadKey==='aircooler'){
      const cooling=String((requirements.cool ?? requirements.c) || '').toLowerCase();
      const h=finitePositive(requirements.cpuH??requirements.ch);
      return cooling!=='liquid'&&h?{custom:true,label:'Recovered Air Cooler Requirement',dims:{l:95,w:95,h}}:null;
    }
    if(payloadKey==='aio'){
      const cooling=String((requirements.cool ?? requirements.c) || '').toLowerCase();
      const rad=finitePositive(requirements.rad??requirements.r);
      if(cooling!=='liquid'||!rad)return null;
      const l=rad>=360?395:(rad>=280?315:(rad>=240?275:155));
      const w=rad===280?140:120;
      return {custom:true,label:`Recovered ${rad}mm AIO`,dims:{l,w,h:27.2},meta:{radiatorSize:rad,fanProfile:String((requirements.fan ?? requirements.f) || 'standard')}};
    }
    if(payloadKey==='ram'){
      const rawRamH=requirements.ramH??requirements.rh;
      const rawRamN=requirements.ramModules??requirements.rn;
      if((rawRamH===undefined||rawRamH===null||String(rawRamH).trim()==='')&&(rawRamN===undefined||rawRamN===null||String(rawRamN).trim()===''))return null;
      const h=finitePositive(rawRamH)||35;
      const modules=Math.max(1,Math.min(8,Number(rawRamN)||2));
      return {custom:true,label:'Recovered RAM Requirement',dims:{l:133,w:7,h},modules,meta:{ramHeight:h,ramHeightKnown:true,ramKitModules:modules}};
    }
    return null;
  };
  const customMeta=(payloadKey,ref,h)=>{
    const base={catalogId:'',...(ref?.meta||{})};
    if(payloadKey==='motherboard')base.formFactor=base.formFactor||normalizeMobo((requirements.mobo ?? requirements.m) || ref?.label);
    if(payloadKey==='psu')base.formFactor=base.formFactor||normalizePsu((requirements.psu ?? requirements.p) || ref?.label);
    if(payloadKey==='ram')Object.assign(base,{ramHeight:h,ramHeightKnown:true,ramKitModules:Number(ref?.modules)||Number(base.ramKitModules)||2});
    if(payloadKey==='aio'){
      base.radiatorSize=Number(base.radiatorSize)||finitePositive(requirements.rad??requirements.r)||0;
      base.fanProfile=base.fanProfile||String((requirements.fan ?? requirements.f) || 'standard');
      base.radFan=base.radFan||base.fanProfile;
    }
    return base;
  };
  const spawnCustom=(payloadKey,trackType,ref,reason='custom')=>{
    if(!ref?.custom)return false;
    const d=ref.dims||{};
    const l=finitePositive(d.l),w=finitePositive(d.w),h=finitePositive(d.h);
    if(!(l&&w&&h))return false;
    const ok=window.loadProceduralHardware?.(l,w,h,String(ref.label||`Custom ${payloadKey}`),true,`${l} x ${w} x ${h} mm`,customColors[trackType]||0x38bdf8,trackType,customMeta(payloadKey,ref,h));
    if(ok)loaded.push({type:payloadKey,custom:true,source:reason});
    return !!ok;
  };
  const importHardware=(payloadKey,trackType,expectedKinds)=>{
    const ref=payload.hardware?.[payloadKey]||null;
    const hit=resolveRef(ref,expectedKinds);
    if(hit?.record){
      const row=payloadKey==='ram' && ref?.modules ? {...hit.record,ramKitModules:ref.modules} : hit.record;
      const ok=loadCatalogHardwareRecord(trackType,row);
      if(ok)loaded.push({type:payloadKey,id:ref.id}); else missing.push({type:payloadKey,id:ref.id,reason:'hardware_rejected'});
      return;
    }
    if(ref?.custom){
      if(!spawnCustom(payloadKey,trackType,ref,'token_custom'))missing.push({type:payloadKey,reason:'custom_hardware_rejected'});
      return;
    }
    // Universal IDs remain authoritative. If an ID is stale/missing after a catalog refresh,
    // preserve the physical build with the explicit requirement envelope carried in schema 2,
    // while keeping the unresolved ID visible as an advisory rather than fuzzy-matching a name.
    const fallback=fallbackRefFromRequirements(payloadKey);
    if(fallback && spawnCustom(payloadKey,trackType,fallback,ref?.id?'requirements_after_unresolved_id':'requirements')){
      if(ref?.id)missing.push({type:payloadKey,id:ref.id,reason:'catalog_id_unresolved_geometry_restored'});
      return;
    }
    if(ref?.id)missing.push({type:payloadKey,id:ref.id,reason:'catalog_id_unresolved'});
  };

  // Motherboard first so socket/RAM attachments have their physical authority before children.
  importHardware('motherboard','motherboard',['motherboard']);
  importHardware('gpu','gpu',['gpu']);
  importHardware('psu','psu',['psu']);
  importHardware('aircooler','aircooler',['aircooler']);
  importHardware('aio','radiator',['radiator','aio']);
  importHardware('ram','ram',['ram']);

  prepareComponentsForFreshSolve('portable_build_import');
  rebuildSimulatorPlan({realign:true,reason:'portable_build_import'});
  const controls=document.getElementById(`spatial_controls_${caseBox.uid}`);
  if(controls)controls.outerHTML=buildCaseSpatialControls(caseBox);
  return {ok:true,loaded,missing,caseId:caseBox.catalogId||null,modeId:caseBox.spatialState.modeId||null,fixedStep:caseBox.spatialState.fixedStep||null};
}

window.importSffBuildBridgePayload=(payload,resolver)=>importSffBuildBridgePayload(payload,resolver||window.resolveSffCatalogId||(()=>null));

window.getSffBuildBridgePayload = function() {
  const caseBox=activeCaseBox();
  const byType=type=>activeBoxes.find(item=>item?.isComponent&&item.trackType===type)||null;
  const compact=comp=>{
    if(!comp)return null;
    const id=String(comp.catalogId||comp.componentMeta?.catalogId||'').trim();
    if(id)return {id};
    return {
      custom:true,
      label:String(comp.displayLabel||comp.trackType||'Custom'),
      dims:{l:Number(comp.nativeSpecs?.l)||0,w:Number(comp.nativeSpecs?.w)||0,h:Number(comp.nativeSpecs?.h)||0}
    };
  };
  const cooler=byType('aircooler');
  const radiator=byType('radiator');
  const ram=byType('ram');
  const mobo=byType('motherboard'),gpu=byType('gpu'),psu=byType('psu');
  const requirement={
    m:mobo?.componentMeta?.formFactor||undefined,
    p:psu?.componentMeta?.formFactor||undefined,
    gl:gpu?Number(gpu.nativeSpecs?.l)||undefined:undefined,
    gt:gpu?Number(gpu.nativeSpecs?.w)||undefined:undefined,
    gh:gpu?Number(gpu.nativeSpecs?.h)||undefined:undefined,
    c:cooler?'air':(radiator?'liquid':undefined),
    ch:cooler?Number(cooler.nativeSpecs?.h)||undefined:undefined,
    r:radiator?Number(radiator.componentMeta?.radiatorSize)||undefined:undefined,
    f:radiator?.componentMeta?.radFan||radiator?.componentMeta?.fanProfile||undefined,
    s:caseBox?.catalogRecord?.name||caseBox?.nativeSpecs?.name||undefined
  };
  return {
    schema:2,
    identitySchema:1,
    case:caseBox ? (caseBox.catalogId?{id:caseBox.catalogId}:{custom:true,label:caseBox.catalogRecord?.name||caseBox.nativeSpecs?.name||'Custom Enclosure',dims:{l:Number(caseBox.nativeSpecs?.l)||0,w:Number(caseBox.nativeSpecs?.w)||0,h:Number(caseBox.nativeSpecs?.h)||0}}) : null,
    modeId:caseBox?.spatialState?.modeId || null,
    fixedStep:caseBox?.spatialState?.fixedStep || null,
    hardware:{
      motherboard:compact(byType('motherboard')),
      gpu:compact(byType('gpu')),
      psu:compact(byType('psu')),
      aircooler:compact(cooler),
      aio:compact(radiator),
      ram:ram ? {...compact(ram),modules:Number(ram.componentMeta?.ramKitModules||ram.ramKitModules)||2} : null
    },
    requirements:requirement
  };
};

window.getRelationalSpatialSnapshot = function() {
  return createSpatialSnapshot({activeBoxes,engineMode:activeEngineMode,activePlan:SFF_SimulatorEngine.activeBlueprint});
};

function bindClearWorkspaceButton() {
    const btn = document.getElementById('clear-workspace-btn'); if (btn) { btn.onclick = function() { clearPendingFanReplacement(); [...activeBoxes].forEach(box => window.removeModelInstance?.(box.uid)); }; }
}



  return {
    activeCaseBox,
    getEngineMode: () => activeEngineMode,
    getActivePlan: () => SFF_SimulatorEngine.activeBlueprint,
    getSolveEpoch: () => simulatorSolveEpoch,
    getSolveFingerprint: () => lastSpatialSolveFingerprint,
    hardwareEligibility,
    hardwareDatasetEligibility,
    refreshHardwareEligibilityUi,
    loadCatalogHardwareRecord,
    rebuildSimulatorPlan,
    buildCaseSpatialControls,
    triggerCaseSpawn,
    proposePositionAxis,
    checkAndAttachToMotherboardSocket,
    checkAndAttachRamToMotherboard,
    checkAndSnapFanToRadiator,
    refreshFanPlacementUi,
    getFanPlacementOptions:(frame,thickness)=>fanPlacementTargets(Number(frame)||120,Number(thickness)||25).map(item=>({id:item.id,label:item.label,kind:item.kind})),
    updateAttachedMoboChildren,
    bindClearWorkspaceButton,
    realignAllComponentsToActiveBlueprint: (...args) => window.realignAllComponentsToActiveBlueprint?.(...args),
    realignComponentToActiveBlueprint: (...args) => window.realignComponentToActiveBlueprint?.(...args),
    removeModelInstance: (...args) => window.removeModelInstance?.(...args),
    loadProceduralHardware: (...args) => window.loadProceduralHardware?.(...args),
    getSpatialSolveDebug: () => window.getSpatialSolveDebug?.(),
    getSffBuildBridgePayload: () => window.getSffBuildBridgePayload?.(),
    importSffBuildBridgePayload,
    getRelationalSpatialSnapshot: () => window.getRelationalSpatialSnapshot?.()
  };
}
